import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useAuth } from "../../contexts/AuthContext";
import { useChat } from "../../contexts/ChatContext";
import Config from "../../config";
import Loading from "../../components/UI/Loading";
import Button from "../../components/UI/Button";
import PaymentModal from "../../components/UI/PaymentModal";
import BasicModal from "../../components/UI/BasicModal";
import WebSocketService from "../../services/WebSocket";
import logoProd from "../../assets/logoProd.png";
import logoProdLight from "../../assets/logoProdLight.png";
import { useIsMobile } from "../../App";
import SubscriptionService from "../../services/SubscriptionService";
import analyticsService from "../../services/AnalyticsService";
import {
  Stethoscope,
  Wind,
  HeartPulse,
  Brain,
  Salad,
  Dumbbell,
  Thermometer,
  Activity,
} from "lucide-react";

const MarkdownLinkWithTooltip = ({ href, children, ...props }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      style={{ position: "relative" }}
    >
      {children}
      <span className={`markdown-link-tooltip${showTooltip ? " visible" : ""}`}>
        {href}
      </span>
    </a>
  );
};

const ChatScreen = ({ readOnly, sidebarOpen, setSidebarOpen }) => {
  const {
    getCurrentChat,
    addMessage,
    updateMessage,
    isTyping: chatIsTyping,
    setIsTyping,
    searchQuery,
    setSearchQuery,
    activeChat,
    resumeChat,
    fetchChatMessages,
    isChatsLoading,
    chats,
    startChat,
    replaceTempChatWithReal,
    rateSearch,
    getCurrentMessages,
    setActiveChat,
  } = useChat();

  const [inputText, setInputText] = useState("");
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [typingMessage, setTypingMessage] = useState("");
  // Per-search_id rating state for instant UI update after rating live search
  const [ratedSearches, setRatedSearches] = useState({});
  const [rateMessage, setRateMessage] = useState("");
  const [showNewChatBtn, setShowNewChatBtn] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [threadLanguage, setThreadLanguage] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");
  const [paymentResponse, setPaymentResponse] = useState({});
  const [subscriptionLimitModal, setSubscriptionLimitModal] = useState(false);
  const [subscriptionExpiredModal, setSubscriptionExpiredModal] =
    useState(false);
  const [paymentRequiredModal, setPaymentRequiredModal] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messageRef = useRef(null);
  const lastFetchedChatIdRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const { theme } = useTheme();
  const { t } = useLocalization();
  const { user } = useAuth();

  const { chatId, shareId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { prepareNewChat } = useChat();

  // Handle shared chat data from navigation state or /shared/:shareId route
  const [sharedViewData, setSharedViewData] = useState(null);

  useEffect(() => {
    if (location.state?.sharedData) {
      setSharedViewData(location.state.sharedData);
      // Clear the state to prevent showing again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch shared data when navigating to /shared/:shareId
  useEffect(() => {
    if (!shareId) return;

    const fetchSharedData = async () => {
      try {
        const response = await fetch(`${Config.baseUrl}share/${shareId}`);
        if (response.ok) {
          const data = await response.json();
          setSharedViewData(data);
        }
      } catch (err) {
        console.error("Failed to fetch shared chat:", err);
      }
    };

    fetchSharedData();
  }, [shareId]);

  useEffect(() => {}, [chatId, activeChat]);

  const handleBuySubscription = async () => {
    navigate("/subscription");
  };

  const confirmPayment = async () => {
    await handleBuySubscription();

    setSubscriptionLimitModal(false);
    setSubscriptionExpiredModal(false);
    setPaymentRequiredModal(false);
    setShowPaywall(false);
  };

  // One-time welcome modal after registration
  const checkWelcomeFlag = useCallback(() => {
    try {
      const flag = localStorage.getItem("show_welcome_after_register");
      if (flag === "1") {
        setShowWelcome(true);
        localStorage.removeItem("show_welcome_after_register");
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    checkWelcomeFlag();
  }, [checkWelcomeFlag]);

  // Re-check when route/chat changes (covers async navigations)
  useEffect(() => {
    checkWelcomeFlag();
  }, [chatId, activeChat, checkWelcomeFlag]);

  const currentChat = getCurrentChat();

  // Determine if this is an archived chat (for /archive/:chatId)
  const isArchiveRoute = window.location.pathname.startsWith("/archive");
  const archivedChat = isArchiveRoute
    ? chats.find((c) => c.id === chatId && c.is_archived)
    : null;

  // Use archivedChat if in archive route, otherwise use currentChat
  const displayChat = isArchiveRoute ? archivedChat : currentChat;

  // Use displayChatWithSearch for rendering
  const displayChatWithSearch = searchData
    ? { ...displayChat, search: searchData }
    : displayChat;

  // Fix: When switching from archive to new chat, clear displayChat if needed
  useEffect(() => {
    if (!isArchiveRoute && chatId === "new") {
      setMessages([]);
      setSearchData(null);
    }
  }, [isArchiveRoute, chatId]);

  useEffect(() => {
    if (!isChatsLoading && chatId) {
      setActiveChat(chatId);
      if (chatId !== "new") {
        // Wait a bit to ensure WebSocket has time to connect
        const setThreadWithDelay = () => {
          if (WebSocketService.isReady()) {
            console.log("ChatScreen: WebSocket is ready, setting thread immediately");
            WebSocketService.setThread(chatId);
          } else {
            console.log("ChatScreen: WebSocket not ready, will set thread after connection");
            WebSocketService.setThread(chatId);
            
            // Also try to set it again after a delay in case connection is in progress
            setTimeout(() => {
              if (WebSocketService.isReady() && WebSocketService.threadId !== chatId) {
                console.log("ChatScreen: WebSocket connected, setting thread now");
                WebSocketService.setThread(chatId);
              }
            }, 1000);
          }
        };
        
        setThreadWithDelay();
        localStorage.setItem("activeChatId", chatId);
      } else {
        localStorage.removeItem("activeChatId");
      }
      setInputText("");
    }
  }, [chatId, isChatsLoading]);

  // Removed: автоматичне перенаправлення на перший чат
  // Тепер завжди відкривається новий чат при завантаженні
  // useEffect(() => {
  //   if (
  //     !isArchiveRoute &&
  //     chatId === "new" &&
  //     chats &&
  //     chats.length > 0 &&
  //     !chats.some((chat) => chat.id === "new")
  //   ) {
  //     setActiveChat(chats[0].id);
  //     navigate(`/chat/${chats[0].id}`);
  //   }
  // }, [isArchiveRoute, chatId, chats, setActiveChat, navigate]);

  const fetchMessages = useCallback(
    async (chatId, preserveLocal = false) => {
      if (!chatId || chatId === "new") {
        setSearchData(null);
        setIsMessagesLoading(false);
        if (!preserveLocal) {
          setMessages([]);
        }
        return;
      }

      setIsMessagesLoading(true);
      try {
        const { success, data } = await fetchChatMessages(chatId);

        if (success && data && Array.isArray(data.messages)) {
          const mappedMessages = data.messages.map((msg) => ({
            id: msg.id.toString(),
            is_user: msg.sender === "user",
            text: msg.content,
            additional_data: msg.additional_data,
            timestamp: msg.timestamp || new Date().toISOString(),
          }));

          setSearchData(data.search || null);
          setThreadLanguage(data.language || null);

          if (preserveLocal) {
            // Merge and deduplicate by id (keep first occurrence)
            setMessages((prevMessages) => {
              const allMessages = [...mappedMessages, ...prevMessages];
              const deduped = [];
              const seen = new Set();
              for (const msg of allMessages) {
                if (!seen.has(msg.id)) {
                  deduped.push(msg);
                  seen.add(msg.id);
                }
              }
              return deduped;
            });
          } else {
            // Deduplicate just in case
            const deduped = [];
            const seen = new Set();
            for (const msg of mappedMessages) {
              if (!seen.has(msg.id)) {
                deduped.push(msg);
                seen.add(msg.id);
              }
            }
            setMessages(deduped);
          }
        } else {
          if (!preserveLocal) {
            setMessages([]);
            setSearchData(null);
          }
        }
      } catch (error) {
        console.error("Failed to fetch messages:", error);
        if (!preserveLocal) {
          setMessages([]);
          setSearchData(null);
        }
      } finally {
        setIsMessagesLoading(false);
      }
    },
    [fetchChatMessages],
  );

  const silentFetchMessages = useCallback(
    async (chatId) => {
      if (!chatId || chatId === "new") return;
      try {
        const { success, data } = await fetchChatMessages(chatId);
        if (success && data && Array.isArray(data.messages)) {
          const mappedMessages = data.messages.map((msg) => ({
            id: msg.id.toString(),
            is_user: msg.sender === "user",
            text: msg.content,
            additional_data: msg.additional_data,
            timestamp: msg.timestamp || new Date().toISOString(),
          }));
          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const newMsgs = mappedMessages.filter((m) => !existingIds.has(m.id));
            if (newMsgs.length === 0) return prev;
            // Insert new messages before any is_loading/is_typing messages
            const stableMessages = prev.filter((m) => !m.is_loading && !m.is_typing);
            const animatingMessages = prev.filter((m) => m.is_loading || m.is_typing);
            return [...stableMessages, ...newMsgs, ...animatingMessages];
          });
        }
      } catch {
        // silent — не показуємо помилку
      }
    },
    [fetchChatMessages],
  );

  useEffect(() => {
    const currentParamChatId = chatId;
    const currentActiveChatId = activeChat;
    let idToFetch = null;
    let preserveLocalOnFetch = false;

    if (currentActiveChatId && currentActiveChatId !== "new") {
      idToFetch = currentActiveChatId;
      // Preserve local messages when transitioning from "new" to real chat
      if (
        lastFetchedChatIdRef.current === null ||
        lastFetchedChatIdRef.current === "new"
      ) {
        preserveLocalOnFetch = true;
      }
    } else if (currentParamChatId && currentParamChatId !== "new") {
      idToFetch = currentParamChatId;
      // Also preserve when transitioning from URL param
      if (
        lastFetchedChatIdRef.current === null ||
        lastFetchedChatIdRef.current === "new"
      ) {
        preserveLocalOnFetch = true;
      }
    }

    if (idToFetch && idToFetch !== lastFetchedChatIdRef.current) {
      // Очищуємо попередній timeout якщо він є
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      
      // Показуємо завантаження при відкритті чату
      setIsMessagesLoading(true);
      
      // Додаємо затримку в 500 мілісекунд перед завантаженням повідомлень
      loadingTimeoutRef.current = setTimeout(async () => {
        await fetchMessages(idToFetch, preserveLocalOnFetch);
        lastFetchedChatIdRef.current = idToFetch;
        loadingTimeoutRef.current = null;
        // Scroll to bottom after messages are loaded
        setTimeout(() => {
          scrollToBottom(true); // Instant scroll when opening chat
        }, 200);
      }, 500);
    } else if (currentParamChatId === "new" || !idToFetch) {
      // Очищуємо timeout якщо він є
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Only clear if we're not in the middle of creating a chat
      if (lastFetchedChatIdRef.current !== null) {
        setMessages([]);
      }
      setSearchData(null);
      lastFetchedChatIdRef.current = null;
      setIsMessagesLoading(false);
    }
  }, [chatId, activeChat, fetchMessages]);

  // useEffect(() => {
  //   // Only set thread if it's a real chat ID
  //   if (activeChat && activeChat !== "new") {
  //     WebSocketService.setThread(activeChat);
  //   }
  // }, [activeChat]);

  useEffect(() => {
    if (!chatIsTyping && typingMessage) {
      setTypingMessage("");
    }
  }, [chatIsTyping, typingMessage]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, typingMessage]);

  // Silent polling every 15s while waiting for bot response
  useEffect(() => {
    const chatIdToFetch = activeChat && activeChat !== "new" ? activeChat : chatId;
    if (chatIsTyping && chatIdToFetch && chatIdToFetch !== "new") {
      pollingIntervalRef.current = setInterval(() => {
        silentFetchMessages(chatIdToFetch);
      }, 15000);
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [chatIsTyping, activeChat, chatId, silentFetchMessages]);

  // Scroll to bottom when messages finish loading (when opening a chat)
  useEffect(() => {
    if (!isMessagesLoading && messages.length > 0) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        scrollToBottom(true); // Instant scroll when opening chat
      }, 150);
    }
  }, [isMessagesLoading, messages.length]);

  // Scroll to bottom when chatId changes (when opening a different chat)
  useEffect(() => {
    if (chatId && chatId !== "new" && messages.length > 0) {
      setTimeout(() => {
        scrollToBottom(true); // Instant scroll when switching chats
      }, 300);
    }
  }, [chatId]);

  useEffect(() => {
    if (activeChat === "new") {
      setMessages([]);
      setIsMessagesLoading(false);
      setSearchData(null);
    }

    setTypingMessage("");
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    setIsTyping(false);
  }, [activeChat, setIsTyping]);

  const animateIntro = useCallback(
    (messageId, introText, onComplete) => {
      if (!introText) {
        if (onComplete) onComplete();
        return;
      }
      let currentText = "";
      let currentIndex = 0;
      const chars = introText.split("");

      const typeNextChar = () => {
        if (currentIndex < chars.length) {
          currentText += chars[currentIndex];

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    typedIntro: currentText,
                    is_intro_typing: true,
                  }
                : msg,
            ),
          );

          currentIndex++;

          let delay = 20;
          const char = chars[currentIndex - 1];
          if (char === "." || char === "!" || char === "?" || char === ";") {
            delay = 70;
          } else if (char === " " || char === "\n") {
            delay = 25;
          }
          setTimeout(typeNextChar, delay);
        } else {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId ? { ...msg, is_intro_typing: false } : msg,
            ),
          );
          if (onComplete) {
            setTimeout(onComplete, 100);
          }
        }
      };
      typeNextChar();
    },
    [],
  );

  const animateSummary = useCallback(
    (messageId, summaryText, onComplete) => {
      if (!summaryText) {
        // No summary - show scale immediately and continue
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, showScale: true } : msg,
          ),
        );
        if (onComplete) onComplete();
        return;
      }
      let currentText = "";
      let currentIndex = 0;
      const chars = summaryText.split("");

      const typeNextChar = () => {
        if (currentIndex < chars.length) {
          currentText += chars[currentIndex];

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    typedSummary: currentText,
                    is_summary_typing: true,
                  }
                : msg,
            ),
          );

          currentIndex++;

          let delay = 20; // slightly faster for summary
          const char = chars[currentIndex - 1];
          if (char === "." || char === "!" || char === "?" || char === ";") {
            delay = 70;
          } else if (char === " " || char === "\n") {
            delay = 25;
          }
          setTimeout(typeNextChar, delay);
        } else {
          // Summary done - show scale, then trigger main text
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? { ...msg, is_summary_typing: false, showScale: true }
                : msg,
            ),
          );
          // Call onComplete callback after summary animation finishes
          if (onComplete) {
            setTimeout(onComplete, 100);
          }
        }
      };
      typeNextChar();
    },
    [],
  );

  const animateTyping = useCallback(
    (messageId, text) => {
      if (!text) return;
      let currentText = "";
      let currentIndex = 0;
      const chars = text.split("");

      const typeNextChar = () => {
        if (currentIndex < chars.length) {
          currentText += chars[currentIndex];

          // Only update the message, never add
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    text: currentText,
                    is_typing: true,
                    is_loading: false,
                  }
                : msg,
            ),
          );

          currentIndex++;

          let delay = 28; // base delay for most characters
          const char = chars[currentIndex - 1];
          if (char === "." || char === "!" || char === "?" || char === ";") {
            delay = 90; // short pause for punctuation
          } else if (char === " " || char === "\n") {
            delay = 32; // slightly longer for spaces/newlines
          }
          typingTimeoutRef.current = setTimeout(typeNextChar, delay);
        } else {
          // When typing is complete, only update the message
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId ? { ...msg, is_typing: false } : msg,
            ),
          );

          setIsTyping(false);
          setTypingMessage("");
        }
      };
      typeNextChar();
    },
    [getCurrentChat, setIsTyping, updateMessage],
  );

  // Animate intro → summary → scale → main text
  const animateMessageWithSummary = useCallback(
    (messageId, messageText, summaryText, introText) => {
      const animateSummaryAndText = () => {
        animateSummary(messageId, summaryText, () => {
          animateTyping(messageId, messageText);
        });
      };

      if (introText) {
        // Animate intro first, then summary, then main text
        animateIntro(messageId, introText, animateSummaryAndText);
      } else {
        // No intro, start with summary
        animateSummaryAndText();
      }
    },
    [animateIntro, animateSummary, animateTyping],
  );

  const handleWSMessage = useCallback(
    (wsAnswer) => {
      const threadId = wsAnswer.thread_id ?? wsAnswer.content?.thread_id;
      const messageText = wsAnswer.message ?? wsAnswer.content?.content;

      // Normalize threadId and activeChat to strings for comparison
      const threadIdStr = threadId ? String(threadId) : null;
      const activeChatStr = activeChat ? String(activeChat) : null;

      // Handle agent status
      if (activeChat === "new" || (threadIdStr && threadIdStr !== activeChatStr)) {
        return;
      }

      if (
        wsAnswer.type === "agent_status" ||
        wsAnswer.type === "agent_question"
      ) {
        setMessages((prev) => {
          const filteredMsgs = prev.filter((msg) => !msg.is_loading);
          // If message with this id exists, update it to trigger animation
          if (filteredMsgs.some((msg) => msg.id === wsAnswer.id)) {
            // Optionally, you can clear text here if you want to animate from empty
            setTimeout(() => {
              animateTyping(wsAnswer.id, messageText);
            }, 100);
            return filteredMsgs.map((msg) =>
              msg.id === wsAnswer.id
                ? {
                    ...msg,
                    is_typing: true,
                    is_loading: false,
                    // text: '', // Uncomment if you want to animate from empty
                  }
                : msg,
            );
          }
          const newMessage = {
            id: wsAnswer.id,
            text: "", // Start with empty for animation
            is_user: false,
            timestamp: new Date().toISOString().split("T"),
            is_typing: true,
            is_loading: false,
            type: wsAnswer.type,
          };
          setTimeout(() => {
            animateTyping(newMessage.id, messageText);
          }, 100);
          return [...filteredMsgs, newMessage];
        });
        return;
      }

      // Handle search answer or others
      if (wsAnswer.type === "search_answer") {
        // Normalize for comparison
        const threadIdStr = threadId ? String(threadId) : null;
        const activeChatStr = activeChat ? String(activeChat) : null;
        
        if (activeChat === "new" || (threadIdStr && threadIdStr !== activeChatStr)) {
          return;
        }
        setIsTyping(true);

        setMessages((prev) => {
          // If message with this id exists, update it to trigger animation
          if (prev.some((msg) => msg.id === wsAnswer.id)) {
            setTimeout(() => {
              animateTyping(wsAnswer.id, messageText);
            }, 100);
            return prev.map((msg) =>
              msg.id === wsAnswer.id
                ? {
                    ...msg,
                    is_typing: true,
                    is_loading: false,
                    // text: '', // Uncomment if you want to animate from empty
                  }
                : msg,
            );
          }
          const stanceAnalysis =
            wsAnswer.stance_analysis ||
            wsAnswer.additional_data?.stance_analysis;
          const introText =
            wsAnswer.intro_text ||
            wsAnswer.additional_data?.intro_text;
          const newMessage = {
            id: wsAnswer.id,
            text: "", // Start with empty for animation
            is_user: false,
            timestamp: new Date().toISOString().split("T"),
            is_typing: true,
            is_loading: false,
            type: wsAnswer.type,
            is_search: true,
            search_id:
              wsAnswer.additional_data?.search_id ||
              wsAnswer.content?.search_id ||
              wsAnswer.id,
            stance_analysis: stanceAnalysis,
            intro_text: introText,
            typedIntro: introText ? "" : undefined, // Start empty for animation
            typedSummary: stanceAnalysis?.summary ? "" : undefined, // Start empty for animation
            language:
              wsAnswer.language ||
              wsAnswer.additional_data?.language ||
              threadLanguage,
          };
          setTimeout(() => {
            animateMessageWithSummary(
              newMessage.id,
              messageText,
              stanceAnalysis?.summary,
              introText,
            );
          }, 100);
          return [...prev, newMessage];
        });
      }
    },
    [activeChat, animateTyping, animateMessageWithSummary, setIsTyping, threadLanguage],
  );

  useEffect(() => {
    WebSocketService.onMessage(handleWSMessage);
    
    // Set up onOpen handler to automatically set thread when WebSocket connects
    WebSocketService.onOpen(() => {
      const currentChatId = chatId || activeChat;
      if (currentChatId && currentChatId !== "new" && WebSocketService.isReady()) {
        WebSocketService.setThread(currentChatId);
      }
    });
    
    return () => {
      // Cleanup if needed
    };
  }, [chatId, activeChat, handleWSMessage]);

  // Cleanup timeout при розмонтуванні компонента
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  const scrollToBottom = (instant = false) => {
    const messagesArea = document.querySelector("[data-messages-area]");
    if (messagesArea) {
      messagesArea.scrollTo({
        top: messagesArea.scrollHeight,
        behavior: instant ? "auto" : "smooth",
      });
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const textToSend = text.trim();
    setInputText("");

    // Create user message
    const userMessage = {
      id: Date.now().toString(),
      text: textToSend,
      is_user: true,
      timestamp: new Date().toISOString(),
    };

    // Create bot loading message
    const botMessageId = (Date.now() + 1).toString();
    const botMessage = {
      id: botMessageId,
      text: "",
      is_user: false,
      timestamp: new Date().toISOString(),
      is_typing: false,
      is_loading: true,
    };

    // Add both messages to local state immediately
    setMessages((prev) => [...prev, userMessage, botMessage]);

    try {
      setIsTyping(true);
      
      // Log chat message to analytics
      analyticsService.logChatMessage(textToSend.length);
      
      const { success, chat: realChat, error, status } = await startChat(textToSend);

      if (success && realChat) {
        lastFetchedChatIdRef.current = realChat.id;
        await addMessage(realChat.id, userMessage);
        
        // Log successful chat start
        analyticsService.logChatStarted();
      } else {
        setMessages((prev) => prev.filter((msg) => msg.id !== botMessageId));
        setIsTyping(false);
        
        // Check if it's a 402 Payment Required error
        if (status === 402 || (error && error.includes && error.includes('402'))) {
          // Замість показу модалки одразу переходимо на сторінку підписки
          await confirmPayment();
        } else {
          console.error("Failed to create chat:", error);
          // Log chat error
          analyticsService.logError('chat_failed', 'failed_to_create_chat');
        }
      }
    } catch (error) {
      setMessages((prev) => prev.filter((msg) => msg.id !== botMessageId));
      setIsTyping(false);
      
      // Check if it's a 402 Payment Required error
      if (error?.response?.status === 402) {
        // Замість показу модалки одразу переходимо на сторінку підписки
        await confirmPayment();
      } else {
        console.error("Chat creation error:", error);
        // Log chat error
        analyticsService.logError('chat_failed', 'exception');
      }
    }
  };

  const sendAnswer = async (text) => {
    if (!text.trim()) return;
    const textToSend = text.trim();
    setInputText("");

    const userMessage = {
      id: Date.now().toString(),
      text: textToSend,
      is_user: true,
      timestamp: new Date().toISOString(),
    };

    const botMessageId = (Date.now() + 1).toString();
    const botMessage = {
      id: botMessageId,
      text: "",
      is_user: false,
      timestamp: new Date().toISOString(),
      is_typing: false,
      is_loading: true,
    };

    // Add both messages to local state immediately
    setMessages((prev) => [...prev, userMessage, botMessage]);

    const chat = getCurrentChat();
    if (!chat || chat.id === "new") {
      console.error("No active chat found");
      setMessages((prev) => prev.filter((msg) => msg.id !== botMessageId));
      return;
    }

    try {
      setIsTyping(true);

      // Add user message to chat context
      await addMessage(chat.id, userMessage);

      const { success } = await resumeChat(chat.id, textToSend);

      if (!success) {
        setMessages((prev) => prev.filter((msg) => msg.id !== botMessageId));
        setIsTyping(false);
        console.error("Failed to resume chat");
      }
      // Bot response will come via WebSocket
    } catch (error) {
      setMessages((prev) => prev.filter((msg) => msg.id !== botMessageId));
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (messages?.length < 1) {
        handleSend(inputText);
      } else {
        sendAnswer(inputText);
      }
    }
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
  };

  const hintQuestions = [
    { text: t("hintQuestion1"), icon: Stethoscope },    // Dermatitis - medical
    { text: t("hintQuestion2"), icon: Wind },           // Bronchitis - lungs/breathing
    { text: t("hintQuestion3"), icon: HeartPulse },     // Gastritis - health
    { text: t("hintQuestion4"), icon: Brain },          // Vitamins for stress - mental
    { text: t("hintQuestion5"), icon: Salad },          // Keto diet - nutrition
    { text: t("hintQuestion6"), icon: Dumbbell },       // Protein for muscle - fitness
    { text: t("hintQuestion7"), icon: Thermometer },    // Sore throat - illness
    { text: t("hintQuestion8"), icon: Activity },       // Pancreas - health
  ];

  const handleHintClick = (question) => {
    const text = question.text;
    const dotsIndex = text.indexOf("...");

    // Replace "..." with space and set cursor after it
    const cleanText = dotsIndex !== -1 ? text.replace("...", " ") : text;
    const cursorPosition = dotsIndex !== -1 ? dotsIndex + 1 : cleanText.length;

    setInputText(cleanText);

    // Focus and set cursor position after state update
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };

  const copyToClipboard = (text, messageId) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopiedMessageId(messageId);
          setTimeout(() => setCopiedMessageId(null), 3000);
        })
        .catch((err) => {
          console.error("Clipboard error:", err);
        });
    } else {
      // fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 3000);
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }
      document.body.removeChild(textarea);
    }
  };

  const SearchNotification = () => {
    if (!searchQuery) return null;

    return (
      <div style={searchNotificationStyles}>
        <span style={searchNotificationIconStyles}>🔍</span>
        <span style={searchNotificationTextStyles}>
          {t("searchingFor")}: <strong>{searchQuery}</strong>
        </span>
        <button
          style={searchNotificationCloseStyles}
          onClick={() => setSearchQuery("")}
          title={t("clearSearch")}
        >
          ×
        </button>
      </div>
    );
  };

  // Update handleRate to accept a searchId parameter
  const handleRate = async (type, searchId) => {
    if (!searchId) return;
    const res = await rateSearch(searchId, type === "up");

    if (res.success) {
      setRatedSearches((prev) => ({
        ...prev,
        [searchId]: type === "up" ? "up" : "down",
      }));
      setShowNewChatBtn(true);
      setRateMessage(
        type === "up" ? t("rateThankYouPositive") : t("rateThankYouNegative"),
      );
      // Persist rating state for this search/chat
      localStorage.setItem(`chat_rated_${searchId}`, "1");

      // --- Update static search answer in local state ---
      setSearchData((prev) =>
        prev && prev.id === searchId
          ? {
              ...prev,
              is_helpful: type === "up" ? true : false,
            }
          : prev,
      );
    } else {
      setRateMessage("");
    }
  };

  // Restore rating state on mount or when chat/search changes
  useEffect(() => {
    if (displayChatWithSearch?.search?.id) {
      const searchId = displayChatWithSearch.search.id;
      const rated = localStorage.getItem(`chat_rated_${searchId}`) === "1";
      setRatedSearches((prev) => ({
        ...prev,
        [searchId]: rated ? "up" : undefined,
      }));
      setShowNewChatBtn(rated);
    } else {
      setRatedSearches({});
      setShowNewChatBtn(false);
    }
  }, [displayChatWithSearch?.search?.id]);

  // Handler for New Chat button (same as sidebar)

  const handleNewChatBtn = () => {
    prepareNewChat();
    setInputText("");
    setMessages([]);
    setIsMessagesLoading(false);
    setSearchData(null);
    setShowNewChatBtn(false);
  };

  // Compute displayMessages: all messages + search answer as last message if present
  // If viewing shared data, show that instead
  let displayMessages = sharedViewData ? [] : messages.map((msg) => {
    // For live search_answer messages, ensure unique React key but keep original id
    if (msg.is_search && msg.type === "search_answer") {
      return { ...msg, _reactKey: `live_search_${msg.id}` };
    }
    return { ...msg, _reactKey: msg.id };
  });

  // Add shared data messages if viewing shared chat
  if (sharedViewData) {
    if (sharedViewData.question) {
      displayMessages.push({
        id: "shared_question",
        _reactKey: "shared_question",
        is_user: true,
        text: sharedViewData.question,
      });
    }
    if (sharedViewData.answer) {
      displayMessages.push({
        id: "shared_answer",
        _reactKey: "shared_answer",
        is_user: false,
        text: sharedViewData.answer,
        is_search: true,
        stance_analysis: sharedViewData.stance_analysis,
        query: sharedViewData.question,
        language: sharedViewData.language,
      });
    }
  }
  if (displayChatWithSearch?.search?.answer) {
    displayMessages.push({
      id: `search_${displayChatWithSearch.search.id}`,
      _reactKey: `static_search_${displayChatWithSearch.search.id}`,
      is_user: false,
      text: displayChatWithSearch.search.answer,
      is_search: true,
      search_id: displayChatWithSearch.search.id,
      is_helpful: displayChatWithSearch.search.is_helpful,
      stance_analysis: displayChatWithSearch.search.stance_analysis,
      intro_text: displayChatWithSearch.search.intro_text,
      query: displayChatWithSearch.query,
      language: threadLanguage,
    });
  }
  // Deduplicate displayMessages by _reactKey before rendering
  {
    const seen = new Set();
    displayMessages = displayMessages.filter((msg) => {
      if (seen.has(msg._reactKey)) return false;
      seen.add(msg._reactKey);
      return true;
    });
    // Further deduplicate by content and sender (is_user)
    const seenContent = new Set();
    displayMessages = displayMessages.filter((msg) => {
      const key = `${msg.text}|${msg.is_user}`;
      if (seenContent.has(key)) return false;
      seenContent.add(key);
      return true;
    });
  }

  // Hide input if static search answer is present OR if any search_answer message is done typing/loading
  const hasLiveSearchAnswer = messages.some(
    (m) => m.type === "search_answer" && !m.is_typing && !m.is_loading,
  );
  // If static search is present, always hide input
  const isInputDisabled =
    !!displayChatWithSearch?.search?.answer || hasLiveSearchAnswer;

  const isMobile = useIsMobile();

  // Styles (keeping the existing styles)
  const containerStyles = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100%",
    backgroundColor: theme.background,
    fontFamily:
      '"Söhne", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    position: "relative",
    overflow: "hidden",
    overflowX: "hidden",
    boxSizing: "border-box",
  };

  const mainContentStyles = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    overflow: "hidden",
    overflowX: "hidden",
    position: "relative",
    boxSizing: "border-box",
  };

  const topBarStyles = {
    height: isMobile ? 56 : 81,
    backgroundColor: theme.surface,
    borderBottom: theme.isDark ? "1px solid #4a4a4a" : "1px solid #e5e5e5",
    display: "flex",
    alignItems: "center",
    padding: isMobile ? "0 8px" : "0 16px",
    gap: isMobile ? 6 : 12,
    position: "relative",
  };

  const hamburgerButtonStyles = {
    background: "none",
    border: "none",
    color: theme.text,
    fontSize: isMobile ? 22 : 25,
    cursor: "pointer",
    padding: isMobile ? 8 : 15,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
  };

  const titleStyles = {
    fontSize: isMobile ? "0.9rem" : "1.5rem",
    fontWeight: isMobile ? 400 : 600,
    color: theme.text,
    padding: isMobile ? "10px" : "20px 20px 20px 0",
    whiteSpace: isMobile && "nowrap",
    overflow: isMobile && "hidden",
    textOverflow: isMobile && "ellipsis",
  };

  const messagesAreaStyles = {
    flex: 1,
    overflowY: "auto",
    backgroundColor: theme.background,
    paddingBottom: isMobile ? "10px" : "20px",
    scrollBehavior: "smooth",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
    boxSizing: "border-box",
    WebkitOverflowScrolling: "touch",
  };

  const messagesContainerStyles = {
    maxWidth: isMobile ? "100%" : "768px",
    width: "100%",
    margin: isMobile ? "0" : "0 auto",
    padding: isMobile ? "4px 8px 4px 8px" : "20px",
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? "6px" : "20px",
    overflowX: "hidden",
    boxSizing: "border-box",
  };

  const messageStyles = (isUser) => ({
    display: "flex",
    gap: isMobile ? "4px" : "12px",
    alignItems: "center",
    position: "relative",
    width: "100%",
    padding: isMobile ? "0 2px" : "4px 0",
    wordWrap: "break-word",
  });

  const avatarStyles = (isUser) => ({
    width: isMobile ? "28px" : "32px",
    height: isMobile ? "28px" : "32px",
    borderRadius: "50%",
    backgroundColor: isUser
      ? theme.primary
      : theme.isDark
        ? "#10a37f"
        : "#19c37d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: isMobile ? "12px" : "14px",
    fontWeight: "bold",
    flexShrink: 0,
    marginBlockEnd: !isUser && "auto",
    margin: isMobile ? "0px 5px" : "0px",
  });

  const messageContentStyles = {
    flex: 1,
    color: theme.text,
    fontSize: isMobile ? "14px" : "16px",
    lineHeight: "1.6",
    wordWrap: "break-word",
    position: "relative",
    padding: isMobile ? "8px 6px" : "12px 24px",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    background: isMobile ? (theme.isDark ? "#181818" : "#f5f5f5") : "inherit",
    borderRadius: isMobile ? "8px" : "14px",
    margin: isMobile ? "0 1px" : "0",
    boxSizing: "border-box",
    overflowWrap: "break-word",
    maxWidth: "100%",
  };

  const copyButtonStyles = {
    position: "absolute",
    top: "0",
    right: isMobile ? "5px" : "-20px",
    padding: isMobile ? "4px 8px" : "6px 12px",
    backgroundColor: theme.surface,
    border: theme.isDark ? "1px solid #4a4a4a" : "1px solid #e5e5e5",
    borderRadius: "6px",
    cursor: "pointer",
    opacity: 0.7,
    transition: "all 0.2s",
    color: theme.text,
    fontSize: isMobile ? "11px" : "13px",
    display: "flex",
    alignItems: "center",
    gap: isMobile ? "4px" : "6px",
    zIndex: 1,
    whiteSpace: "nowrap",
  };

  const inputAreaStyles = {
    backgroundColor: theme.surface,
    borderTop: theme.isDark ? "1px solid #4a4a4a" : "1px solid #e5e5e5",
    padding: isMobile ? "8px" : "16px",
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    boxShadow: theme.isDark
      ? "0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)"
      : "0 -4px 6px -1px rgba(0, 0, 0, 0.05), 0 -2px 4px -1px rgba(0, 0, 0, 0.03)",
  };

  const inputContainerStyles = {
    maxWidth: isMobile ? "100%" : "768px",
    width: "100%",
    margin: isMobile ? "0" : "0 auto",
    position: "relative",
    backgroundColor: theme.background,
    border: theme.isDark ? "1px solid #4a4a4a" : "1px solid #d1d5db",
    borderRadius: "12px",
    boxShadow: theme.isDark
      ? "0 2px 4px rgba(0,0,0,0.3)"
      : "0 2px 4px rgba(0,0,0,0.1)",
    boxSizing: "border-box",
    overflowX: "hidden",
  };

  const textareaStyles = {
    width: "100%",
    minHeight: isMobile ? "20px" : "24px",
    maxHeight: isMobile ? "120px" : "200px",
    padding: isMobile ? "8px 36px 8px 10px" : "12px 48px 12px 16px",
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    color: theme.text,
    fontSize: isMobile ? "16px" : "16px", // 16px minimum to prevent zoom on iOS
    lineHeight: "1.5",
    resize: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
    WebkitAppearance: "none",
    WebkitTapHighlightColor: "transparent",
  };

  const sendButtonStyles = {
    position: "absolute",
    right: isMobile ? "4px" : "8px",
    bottom: isMobile ? "4px" : "8px",
    width: isMobile ? "28px" : "32px",
    height: isMobile ? "28px" : "32px",
    backgroundColor: inputText.trim() ? theme.primary : "transparent",
    border: "none",
    borderRadius: "20px",
    color: inputText.trim() ? "white" : theme.textSecondary,
    cursor: inputText.trim() ? "pointer" : "default",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    fontSize: isMobile ? "14px" : "16px",
  };

  const welcomeStyles = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: theme.text,
    textAlign: "center",
    padding: isMobile ? "20px 8px" : "40px 20px",
  };

  const welcomeTitleStyles = {
    fontSize: isMobile ? "22px" : "32px",
    fontWeight: "600",
    marginBottom: isMobile ? "10px" : "16px",
    background: theme.isDark
      ? "linear-gradient(135deg, #10a37f 0%, #1a73e8 100%)"
      : "linear-gradient(135deg, #19c37d 0%, #4285f4 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
  };

  const welcomeSubtitleStyles = {
    fontSize: isMobile ? "14px" : "16px",
    fontWeight: "400",
    color: theme.textSecondary,
    marginBottom: isMobile ? "16px" : "24px",
    lineHeight: "1.5",
    textAlign: "center",
    maxWidth: "600px",
    margin: "0 auto",
  };

  const hintBoxStyles = {
    display: "flex",
    flexWrap: "wrap",
    gap: isMobile ? "8px" : "10px",
    justifyContent: "center",
    maxWidth: "768px",
    width: "100%",
    marginTop: isMobile ? "16px" : "24px",
    padding: isMobile ? "0 12px" : "0",
  };

  const hintItemStyles = {
    display: "flex",
    alignItems: "center",
    gap: isMobile ? "8px" : "10px",
    padding: isMobile ? "10px 14px" : "12px 18px",
    backgroundColor: theme.surface,
    border: theme.isDark ? "1px solid #4a4a4a" : "1px solid #e5e5e5",
    borderRadius: "24px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontSize: isMobile ? "13px" : "14px",
    color: theme.text,
    textAlign: "left",
    maxWidth: isMobile ? "100%" : "calc(50% - 5px)",
  };

  const searchNotificationStyles = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    backgroundColor: theme.isDark ? "#1a5d4a" : "#e6f4ea",
    borderBottom: theme.isDark ? "1px solid #4ade80" : "1px solid #137333",
    color: theme.isDark ? "#4ade80" : "#137333",
    fontSize: "14px",
  };

  const searchNotificationIconStyles = {
    fontSize: "16px",
  };

  const searchNotificationTextStyles = {
    flex: 1,
  };

  const searchNotificationCloseStyles = {
    background: "none",
    border: "none",
    fontSize: "18px",
    color: "inherit",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "4px",
  };

  const logoSrc = theme.isDark ? logoProdLight : logoProd;

  // Modal component
  const DisclaimerModal = ({ open, onConfirm, onCancel, text }) => {
    if (!open) return null;
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0,0,0,0.35)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            backgroundColor: theme.surface,
            borderRadius: "12px",
            padding: "24px",
            maxWidth: "500px",
            width: "90%",
            border: `1px solid ${theme.border}`,
            maxHeight: "80vh",
            overflow: "auto",
            color: theme.text,
          }}
        >
          <div style={{ fontSize: 18, marginBottom: 24, textAlign: "center" }}>
            {text}
          </div>
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
            }}
          >
            <Button
              size={isMobile ? "small" : "medium"}
              variant="secondary"
              onClick={onCancel}
            >
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              size={isMobile ? "small" : "medium"}
              variant="primary"
              onClick={onConfirm}
              className="modal-primary-button modal-button-glow"
            >
              OK
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Intercept send for first message
  const handleSend = async () => {
    const hasExistingChat =
      (chatId && chatId !== "new") ||
      (activeChat && activeChat !== "new") ||
      (messages && messages.length > 0);

    if (hasExistingChat) {
      return sendAnswer(inputText);
    } else {
      const isStartingNew = messages.length === 0 || chatId === "new";
      const subscriptionInfo = await SubscriptionService.getSubscriptionInfo();

      // Handle new chat creation limits
      if (isStartingNew && chats?.length > 3) {
        // Check if subscription is expired (had subscription but expired)
        if (
          subscriptionInfo?.is_valid === false &&
          subscriptionInfo?.expiration_date === null &&
          subscriptionInfo?.questions_remaining === 0
        ) {
          return setSubscriptionLimitModal(true);
        } else if (
          subscriptionInfo?.is_valid === false &&
          subscriptionInfo?.expiration_date !== null &&
          subscriptionInfo?.questions_remaining === 0
        ) {
          return setSubscriptionLimitModal(true);
        } else if (
          subscriptionInfo?.is_valid === false &&
          subscriptionInfo?.expiration_date !== null &&
          subscriptionInfo?.questions_remaining !== 0
        ) {
          return setSubscriptionExpiredModal(true);
        } else if (
          chats?.length <= 1 &&
          chatId === "new" &&
          messages.length < 1
        ) {
          setPendingMessage(inputText);
          setShowDisclaimer(true);
          return sendAnswer(inputText);
        }
      }

      // Handle first message with disclaimer
      if (chats?.length <= 1 && chatId === "new" && messages.length < 1) {
        setPendingMessage(inputText);
        setShowDisclaimer(true);
        return sendAnswer(inputText);
      }

      // Default: send message normally (valid subscription or less than 3 chats)
      return sendMessageAndClear();

      // Helper function
      function sendMessageAndClear() {
        sendMessage(inputText);
        setPendingMessage("");
        setInputText("");
      }
    }
  };

  const handleDisclaimerConfirm = async () => {
    setShowDisclaimer(false);

    try {
      sendMessage(pendingMessage);
      setPendingMessage("");
      setInputText("");
    } catch (e) {}
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimer(false);
    setPendingMessage("");
    setInputText("");
  };

  // Utility to extract all unique URLs from a string
  function extractUrls(text) {
    if (!text) return [];
    // Improved URL regex: matches http(s) URLs and avoids trailing punctuation
    const urlRegex = /(https?:\/\/[^\s\]\)\"]+)/gi;
    const matches = text.match(urlRegex);
    if (!matches) return [];
    // Remove trailing punctuation and deduplicate, only keep valid URLs
    const cleaned = [
      ...new Set(
        matches
          .map((url) => url.replace(/[.,;:!?)]*$/, ""))
          .filter((url) => {
            try {
              new URL(url);
              return true;
            } catch {
              return false;
            }
          }),
      ),
    ];
    return cleaned;
  }

  return (
    <>
      <style>
        {`
          /* Ensure root and body take full height */
          html, body, #root {
            height: 100%;
            margin: 0;
            padding: 0;
            overflow: hidden;
          }

          /* Ensure the chat container takes full height */
          .chat-container {
            display: flex;
            flex-direction: column;
            width: 100%;
            position: relative;
            overflow: hidden;
            min-height: 100vh;
          }

          @supports (height: 100dvh) {
            .chat-container {
              min-height: 100dvh;
            }
          }

          @media (max-width: 768px) {
            html, body, #root {
              overflow-y: auto;
            }

            .chat-container {
              overflow-x: hidden;
            }
          }

          /* Message container styles */
          .message-container {
            opacity: 1;
            transition: opacity 0.2s ease-out;
            width: 100%;
            position: relative;
            padding-bottom: 24px;
            display: flex;
            align-items: center;
          }

          .message-container.typing {
            opacity: 0.9;
          }

          .markdown-content {
            transition: opacity 0.2s ease-out;
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .typing-message {
            opacity: 1;
            transition: opacity 0.2s ease-out, transform 0.1s ease-out;
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-height: 20px;
          }

          .typing-message p {
            margin: 0;
            white-space: pre-wrap;
            word-wrap: break-word;
            position: relative;
            transition: all 0.1s ease-out;
            animation: textAppear 0.1s ease-out;
          }

          @keyframes textAppear {
            from {
              opacity: 0.8;
            }
            to {
              opacity: 1;
            }
          }

          /* Ensure all markdown content is vertically centered */
          .markdown-content p,
          .markdown-content ul,
          .markdown-content ol,
          .markdown-content blockquote,
          .markdown-content pre {
            margin: 5px;
            padding: 0;
          }

          .markdown-content table {
            border-collapse: collapse;
            width: 100%;
            background: "inherit";
            margin: 12px 0;
            font-size: 15px;
            box-shadow: 0 2px 8px #0001;
          }

          .markdown-content th,
          .markdown-content td {
            border: 1px solid #bdbdbd;
            padding: 8px 12px;
            text-align: left;
            background: inherit;
          }

          .markdown-content th {
            background: ${theme.isDark ? "#1a5d4a" : "#e6f4ea"};
            font-weight: 600;
            color: ${theme.isDark ? "#4ade80" : "#137333"};
          }

          .markdown-content tr:nth-child(even) td {
            background: ${theme.isDark ? "#223c2c" : "#f1f8e9"};
          }

          .markdown-content > *:first-child {
            margin-top: 0;
          }

          .markdown-content > *:last-child {
            margin-bottom: 0;
          }

          /* Rest of the existing styles */
          @keyframes typing {
            0%, 60%, 100% { transform: translateY(0); }
            30% { transform: translateY(-10px); }
          }
          
          .suggestion-item:hover {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f8f9fa"} !important;
            border-color: ${theme.primary} !important;
          }

          .message-container {
            position: relative;
          }

          .copy-button {
            opacity: 0.7;
            transition: all 0.2s ease;
          }

          .message-container:hover .copy-button {
            opacity: 1;
          }

          .copy-button:hover {
            opacity: 1 !important;
            background-color: ${theme.isDark ? "#2a2a2a" : "#f8f9fa"} !important;
          }

          @keyframes copySuccess {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }

          .copy-success {
            animation: copySuccess 0.3s ease;
            background-color: ${theme.isDark ? "#1a5d4a" : "#e6f4ea"} !important;
            color: ${theme.isDark ? "#4ade80" : "#137333"} !important;
            border-color: ${theme.isDark ? "#4ade80" : "#137333"} !important;
          }

          mark {
            background-color: ${theme.isDark ? "#ffc107" : "#ffeb3b"} !important;
            color: ${theme.isDark ? "#000" : "#000"} !important;
            padding: 1px 2px;
            border-radius: 2px;
            font-weight: 600;
          }

          .search-notification-close:hover {
            background-color: rgba(255, 255, 255, 0.2) !important;
          }

          /* Markdown styles */
          .markdown-content {
            line-height: 1.6;
            position: relative;
          }

          .markdown-content.typing {
            min-height: 32px;
            transition: min-height 0.2s, height 0.2s;
          }

          @keyframes markdownAppear {
            from {
              opacity: 0.95;
            }
            to {
              opacity: 1;
            }
          }

          .markdown-content code {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f8f9fa"}; /* Corrected inline dynamic style */
            padding: 2px 4px;
            border-radius: 4px;
            font-size: 14px;
            font-family: 'Fira Code', monospace;
            transition: all 0.1s ease-out;
          }

          .markdown-content pre {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f8f9fa"};
            padding: 12px;
            border-radius: 6px;
            overflow-x: auto;
            margin: 12px 0;
            transition: all 0.1s ease-out;
          }

          .markdown-content pre code {
            background-color: transparent;
            padding: 0;
            font-size: 14px;
          }

          .markdown-content ul {
            align-self: center;
            width: 85%;
            transition: all 0.1s ease-out;
          }

          .markdown-content li {
            margin-left: 20px
          }

          .markdown-content a {
            color: ${theme.primary};
            text-decoration: none;
          }

          .markdown-content a:hover {
            text-decoration: underline;
          }

          .markdown-content img {
            max-width: 100%;
            height: auto;
            border-radius: 6px;
            margin: 12px 0;
          }

          .markdown-content hr {
            border: none;
            border-top: 1px solid ${theme.isDark ? "#4a4a4a" : "#e5e5e5"};
            margin: 16px 0;
          }

          /* Typing cursor animation */
          .typing-cursor {
            display: inline-block;
            width: 2px;
            height: 1.2em;
            background-color: ${theme.text};
            margin-left: 2px;
            animation: blink 1s infinite;
            vertical-align: middle;
            opacity: 1;
            transition: opacity 0.2s ease;
            position: relative;
            z-index: 1;
            pointer-events: none;
          }

          .message-container:not(.typing) .typing-cursor {
            opacity: 0;
          }

          /* Ensure cursor appears at the end of markdown content */
          .markdown-content p:last-child {
            position: relative;
          }

          .markdown-content p.last-paragraph {
            position: relative;
          }

          /* Better cursor positioning for markdown */
          .markdown-content .typing-cursor {
            position: absolute !important;
            bottom: 2px !important;
            right: -6px !important;
          }

          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }

          /* Keep other typing animation styles */
          .typing-message {
            opacity: 1;
            transition: opacity 0.3s ease-out;
          }

          .typing-message.fade-out {
            opacity: 0;
            transform: translateY(-2px);
          }

          .markdown-content {
            transition: opacity 0.3s ease-in;
          }

          .markdown-content.fade-in {
            opacity: 1;
            transform: translateY(0);
          }

          .message-container {
            opacity: 1;
            animation: messageAppear 0.3s ease-out forwards;
            width: 100%;
            transition: all 0.2s ease-out;
          }

          .message-container.loading {
            animation: none;
            transition: all 0.3s ease-out;
          }

          .message-container.typing {
            transition: opacity 0.3s ease-out, transform 0.2s ease-out;
          }

          .message-container.fade-out {
            opacity: 0;
          }

          @keyframes messageAppear {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .message-container.typing {
            animation: typingAppear 0.2s ease-out;
          }

          @keyframes typingAppear {
            from {
              opacity: 0;
              transform: translateY(5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Loading dots animation */
          .loading-dots {
            position: absolute;
            top: 50%;
            left: 0;
            transform: translateY(-50%);
            display: flex;
            gap: 4px;
            align-items: center;
            height: 16px;
          }

          .loading-dot {
            width: 4px;
            height: 4px;
            background-color: ${theme.text};
            border-radius: 50%;
            animation: loadingDot 1.4s infinite ease-in-out;
          }

          .loading-dot:nth-child(1) { animation-delay: -0.32s; }
          .loading-dot:nth-child(2) { animation-delay: -0.16s; }

          @keyframes loadingDot {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }

          .hamburger-btn:hover {
            background-color: ${theme.isDark ? "#2a2a2a" : "#f0f0f0"} !important;
          }

          .markdown-link-tooltip {
            position: absolute;
            z-index: 100;
            background: #222;
            color: #fff;
            padding: 6px 12px;
            border-radius: 6px;
            font-size: 13px;
            white-space: pre-line;
            box-shadow: 0 2px 8px #0005;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.18s;
            left: 50%;
            transform: translateX(-50%);
            bottom: 120%;
            max-width: 320px;
          }
          .markdown-link-tooltip.visible {
            opacity: 1;
            pointer-events: auto;
          }
          .markdown-content a {
            position: relative;
          }

          .rate-btn {
            background: #fff;
            border: 2px solid ${theme.isDark ? "#137333" : "#19c37d"};
            border-radius: 50%;
            font-size: 2rem;
            width: 48px;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(25,195,125,0.08);
            color: #222;
            cursor: pointer;
            margin: 0 8px;
            transition: background 0.18s, box-shadow 0.18s, transform 0.12s, border 0.18s;
            outline: none;
          }
          .rate-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .rate-btn:hover:not(:disabled), .rate-btn:focus-visible:not(:disabled) {
            background: ${theme.isDark ? "#19c37d" : "#e6f4ea"};
            box-shadow: 0 4px 16px rgba(25,195,125,0.18);
            border-color: ${theme.isDark ? "#4ade80" : "#137333"};
          }
          .rate-btn:active:not(:disabled) {
            background: ${theme.isDark ? "#137333" : "#b2e5c2"};
            border-color: ${theme.isDark ? "#19c37d" : "#19c37d"};
            transform: scale(0.92);
          }

          @media (max-width: 768px) {
            .chat-container { padding: 0 !important; }
            .message-container { padding-bottom: 8px !important; }
            .markdown-content { font-size: 16px !important; border-radius: 8px !important; padding: 8px 10px !important; margin: 0 2px !important; }
            .copy-button { font-size: 11px !important; padding: 4px 8px !important; }
            .suggestion-item { padding: 10px !important; }
            .markdown-content pre { font-size: 14px !important; }
            .input-area { margin-top: 4px !important; }
          }
          @media (max-width: 400px) {
            .chat-container { padding: 0 !important; }
            .message-container { padding-bottom: 4px !important; }
            .markdown-content { font-size: 16px !important; border-radius: 6px !important; padding: 6px 4px !important; margin: 0 1px !important; }
            .copy-button { font-size: 10px !important; padding: 3px 6px !important; }
            .suggestion-item { padding: 7px !important; font-size: 12px !important; }
            .markdown-content pre { font-size: 13px !important; }
            .input-area { margin-top: 2px !important; }
            textarea { font-size: 16px !important; padding: 6px 28px 6px 6px !important; }
          }
        `}
      </style>
      <div style={containerStyles} className="chat-container">
        {/* Top Bar */}
        {!isArchiveRoute && (
          <div style={topBarStyles}>
            {isMobile && (
              <button
                className="hamburger-btn"
                style={hamburgerButtonStyles}
                onClick={() => setSidebarOpen && setSidebarOpen(!sidebarOpen)}
                title={t("toggleSidebar") || "Toggle sidebar"}
              >
                ☰
              </button>
            )}
            <span style={{ ...titleStyles, flex: 1 }}>
              {sharedViewData ? (t("sharedChat") || "Спільний чат") : (displayChatWithSearch?.title || t("newChat"))}
            </span>
            <img
              src={logoSrc}
              alt="Logo"
              style={{
                height: isMobile ? 120 : 200,
                width: isMobile ? 150 : 300,
                marginLeft: isMobile ? 8 : 16,
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </div>
        )}

        {/* Search Notification */}
        <SearchNotification />

        {/* Main Content */}
        <div style={mainContentStyles}>
          {/* Messages Area */}
          <div style={messagesAreaStyles} data-messages-area>
            {isChatsLoading || isMessagesLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Loading
                  fullScreen={false}
                  text={t("loading") || "Loading..."}
                />
              </div>
            ) : (!displayChatWithSearch ||
                displayChatWithSearch.id === "new") &&
              messages.length === 0 &&
              !chatIsTyping &&
              !typingMessage &&
              !sharedViewData ? (
              <div style={welcomeStyles}>
                <h1 style={welcomeTitleStyles}>{t("howCanIHelp")}</h1>
                <p style={welcomeSubtitleStyles}>{t("howCanIHelpSubtitle")}</p>
                <div style={hintBoxStyles}>
                  {hintQuestions.map((question, index) => {
                    // const IconComponent = question.icon;
                    const showIcons = false; // Toggle to enable icons
                    return (
                      <div
                        key={index}
                        className="hint-item"
                        style={hintItemStyles}
                        onClick={() => handleHintClick(question)}
                      >
                        {showIcons && question.icon && (
                          <question.icon size={isMobile ? 16 : 18} style={{ flexShrink: 0 }} />
                        )}
                        <span>{question.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={messagesContainerStyles}>
                {(displayMessages || []).map((message, idx) => {
                  const isSearch = message.is_search;
                  // Find the last search answer message in messages
                  const lastSearchMsg = [...messages]
                    .reverse()
                    .find((m) => m.is_search);
                  const lastSearchMsgId = lastSearchMsg?.id;
                  const lastSearchMsgDone =
                    lastSearchMsg &&
                    !lastSearchMsg.is_typing &&
                    !lastSearchMsg.is_loading;
                  const lastSearchMsgRated =
                    lastSearchMsg &&
                    lastSearchMsg.id &&
                    localStorage.getItem(`chat_rated_${lastSearchMsg.id}`) ===
                      "1";

                  // For static search answer after reload
                  const staticSearch = displayChatWithSearch?.search;
                  const staticSearchMsgId = staticSearch
                    ? `search_${staticSearch.id}`
                    : null;
                  // Use is_helpful for static search answer
                  const staticSearchRated =
                    staticSearch && staticSearch.is_helpful !== null;

                  // Is this the last live search message?
                  const isLastSearch =
                    isSearch &&
                    message.id === lastSearchMsgId &&
                    lastSearchMsgDone;
                  // Is this the static search answer after reload?
                  const isStaticSearch =
                    isSearch && message.id === staticSearchMsgId;
                  // Per-message rated state (live search only)
                  const thisMsgRated =
                    ratedSearches[message.search_id] ||
                    (message.search_id &&
                      localStorage.getItem(
                        `chat_rated_${message.search_id}`,
                      ) === "1") ||
                    (message.id &&
                      localStorage.getItem(`chat_rated_${message.id}`) === "1");
                  // For static search: use is_helpful, for live: use localStorage
                  let showRating = false;
                  let showThankYou = false;
                  let thankYouText = "";
                  if (isStaticSearch && staticSearch) {
                    if (staticSearch.is_helpful === null) {
                      showRating = true;
                    } else {
                      showThankYou = true;
                      thankYouText = staticSearch.is_helpful
                        ? t("rateThankYouPositive")
                        : t("rateThankYouNegative");
                    }
                  } else {
                    // Live search answer logic (old)
                    showRating = isLastSearch && !thisMsgRated;
                    showThankYou = isLastSearch && thisMsgRated;
                    thankYouText = rateMessage;
                  }
                  // For live search, show thank you UI immediately after rating
                  if (!isStaticSearch && isLastSearch && thisMsgRated) {
                    showThankYou = true;
                    thankYouText =
                      ratedSearches[message.search_id] === "up"
                        ? t("rateThankYouPositive")
                        : t("rateThankYouNegative");
                  }
                  return (
                    <div
                      key={message._reactKey}
                      style={{
                        ...messageStyles(message.is_user),
                      }}
                      className={`message-container ${message.is_typing ? "typing" : ""} ${message.is_loading ? "loading" : ""}`}
                      ref={messageRef}
                    >
                      {/* Аватар для всіх повідомлень */}
                      <div style={avatarStyles(message.is_user)}>
                        {message.is_user ? user?.name?.charAt(0) || "U" : "AI"}
                      </div>
                      {/* Контент повідомлення */}
                      <div
                        style={{
                          ...messageContentStyles,
                          position: "relative",
                          ...(isSearch && {
                            border: "2px solid rgb(25, 195, 125)",
                            background: theme.isDark
                              ? "rgb(26, 93, 74)"
                              : "#C5F3C5",
                            padding: "10px 100px 10px 30px",
                          }),
                          ...(message.is_typing && {
                            minHeight: isMobile ? 28 : 32,
                          }),
                        }}
                        className={`markdown-content${message.is_typing ? " typing" : ""}`}
                      >
                        {/* AI-повідомлення (Markdown завжди) */}
                        {!message.is_user && (
                          <div style={{ position: "relative" }}>
                            {message.is_loading ? (
                              <div
                                className="loading-dots"
                                style={{
                                  height: 20,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  marginLeft: 2,
                                }}
                              >
                                <span className="loading-dot" />
                                <span className="loading-dot" />
                                <span className="loading-dot" />
                              </div>
                            ) : (
                              <>
                                {/* Intro Text */}
                                {message.intro_text && (
                                  <p style={{
                                    fontSize: 16,
                                    lineHeight: 1.5,
                                    color: theme.text,
                                    marginBottom: 20,
                                    marginTop: 0,
                                  }}>
                                    {message.typedIntro !== undefined
                                      ? message.typedIntro
                                      : message.intro_text}
                                    {message.is_intro_typing && <span className="typing-cursor" />}
                                  </p>
                                )}

                                {/* Stance Analysis Visualization */}
                                {message.stance_analysis && message.stance_analysis.weighted_percentages && (
                                  <div style={{ marginBottom: 24 }}>
                                    {/* Summary */}
                                    {message.stance_analysis.summary && (
                                      <p style={{
                                        fontSize: 16,
                                        lineHeight: 1.5,
                                        color: theme.text,
                                        marginBottom: 20,
                                        marginTop: 0,
                                      }}>
                                        {(() => {
                                          // Use typedSummary during animation, full summary otherwise
                                          const summary = message.typedSummary !== undefined
                                            ? message.typedSummary
                                            : message.stance_analysis.summary;
                                          if (!summary) return null;
                                          const commaIndex = summary.indexOf(',');
                                          if (commaIndex > 0 && commaIndex < 50) {
                                            return (
                                              <>
                                                <strong style={{ fontWeight: 600 }}>{summary.slice(0, commaIndex)}</strong>
                                                {summary.slice(commaIndex)}
                                                {message.is_summary_typing && <span className="typing-cursor" />}
                                              </>
                                            );
                                          }
                                          return (
                                            <>
                                              {summary}
                                              {message.is_summary_typing && <span className="typing-cursor" />}
                                            </>
                                          );
                                        })()}
                                      </p>
                                    )}

                                    {/* Scale section - shows after summary animation or immediately for static messages */}
                                    {(message.showScale === true || message.typedSummary === undefined) && (
                                      <>
                                        {/* Question with article count */}
                                        <div style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 12,
                                          marginBottom: 16,
                                          flexWrap: "wrap",
                                        }}>
                                          <span style={{
                                            fontWeight: 600,
                                            fontSize: 16,
                                            color: theme.text,
                                          }}>
                                            {message.query || displayChatWithSearch?.query || ""}
                                          </span>

                                        </div>

                                        {/* Stacked bar chart */}
                                    {(() => {
                                      const wp = message.stance_analysis.weighted_percentages;
                                      const articles = message.stance_analysis.article_stances || [];

                                      // Count articles per stance
                                      const counts = {
                                        yes: articles.filter(a => a.stance === "yes").length,
                                        likely: articles.filter(a => a.stance === "likely").length,
                                        inconclusive: articles.filter(a => a.stance === "inconclusive").length,
                                        no: articles.filter(a => a.stance === "no").length,
                                      };

                                      const total = wp.yes + wp.likely + wp.inconclusive + wp.no;
                                      if (total === 0) return null;

                                      const colors = {
                                        yes: "#4ade80",
                                        likely: "#facc15",
                                        inconclusive: "#fb923c",
                                        no: "#ef4444",
                                      };

                                      const segments = [
                                        { key: "yes", value: wp.yes, count: counts.yes, color: colors.yes },
                                        { key: "likely", value: wp.likely, count: counts.likely, color: colors.likely },
                                        { key: "inconclusive", value: wp.inconclusive, count: counts.inconclusive, color: colors.inconclusive },
                                        { key: "no", value: wp.no, count: counts.no, color: colors.no },
                                      ].filter(s => s.value > 0);

                                      return (
                                        <div className="stance-bar-container">
                                          {segments.map((segment) => (
                                            <div
                                              key={segment.key}
                                              className={`stance-bar-segment stance-${segment.key} ${segment.key === "yes" || segment.key === "no" ? "text-light" : "text-dark"}`}
                                              style={{ flex: segment.value }}
                                            >
                                              {`${segment.value}%`}
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })()}

                                    {/* Legend - Foldable */}
                                    <details className={`stance-details ${theme.isDark ? "dark" : "light"}`}>
                                      <summary className="stance-details-summary">
                                        <span className="stance-details-arrow">▶</span>
                                        {t("allDetails", {}, message.language)}
                                      </summary>
                                      <div className={`stance-legend-content ${theme.isDark ? "dark" : "light"}`}>
                                        {[
                                          { key: "yes", labelKey: "stanceYes" },
                                          { key: "likely", labelKey: "stanceLikely" },
                                          { key: "inconclusive", labelKey: "stanceInconclusive" },
                                          { key: "no", labelKey: "stanceNo" },
                                        ].map((item) => {
                                          const value = message.stance_analysis.weighted_percentages[item.key];
                                          if (value === 0) return null;
                                          return (
                                            <div key={item.key} className="stance-legend-item">
                                              <div className={`stance-legend-dot stance-${item.key}`} />
                                              <span className="stance-legend-label">
                                                {t(item.labelKey, {}, message.language)}
                                              </span>
                                              <span className="stance-legend-value">
                                                {value}%
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </details>
                                      </>
                                    )}
                                  </div>
                                )}
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm, remarkMath]}
                                  rehypePlugins={[rehypeKatex]}
                                  components={{
                                    p: ({ children }) => (
                                      <p
                                        style={{
                                          margin: "0 0 1em 0", // bottom margin so paragraphs separate
                                          maxWidth: "100%",
                                          whiteSpace: "pre-wrap", // preserve \n inside a paragraph
                                        }}
                                      >
                                        {children}
                                      </p>
                                    ),
                                    a: MarkdownLinkWithTooltip,
                                  }}
                                >
                                  {message.text || ""}
                                </ReactMarkdown>
                                {message.is_typing && (
                                  <span
                                    className="typing-cursor"
                                    style={{
                                      position: "absolute",
                                      bottom: "2px",
                                      right: "-6px",
                                    }}
                                  ></span>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {/* User-повідомлення (звичайний текст) */}
                        {message.is_user && (
                          <p style={{ margin: 0 }}>{message.text}</p>
                        )}
                        {/* Copy button for normal AI messages */}
                        {!message.is_user && !message.is_typing && (
                          <button
                            className={`copy-button ${copiedMessageId === message.id ? "copy-success" : ""}`}
                            style={{
                              ...copyButtonStyles,
                              ...(isSearch && {
                                top: "5px",
                                right: "5px",
                                padding: "10px",
                              }),
                            }}
                            onClick={() => {
                              const summary = message.stance_analysis?.summary || "";
                              const text = message.text || "";
                              copyToClipboard(summary ? `${summary}\n\n${text}` : text, message.id);
                            }}
                            title={t("copyMessage")}
                          >
                            {copiedMessageId === message.id ? (
                              // Check icon
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M7.629 15.29a1 1 0 0 1-1.415 0l-3.207-3.207a1 1 0 1 1 1.415-1.415l2.5 2.5 6.67-6.67a1 1 0 1 1 1.415 1.415l-7.378 7.377z" />
                              </svg>
                            ) : (
                              // Copy icon
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="7"
                                  y="7"
                                  width="9"
                                  height="9"
                                  rx="2"
                                  fill="currentColor"
                                />
                                <rect
                                  x="4"
                                  y="4"
                                  width="9"
                                  height="9"
                                  rx="2"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  fill="none"
                                />
                              </svg>
                            )}
                          </button>
                        )}
                        {/* Links and Rating UI for search answer */}
                        {isSearch &&
                          (showRating || showThankYou) &&
                          !isArchiveRoute && (
                            <>
                              {/* Links section before rating */}
                              {(() => {
                                const urls = extractUrls(message.text);
                                if (urls.length === 0) return null;
                                return (
                                  <div
                                    style={{
                                      background: theme.isDark
                                        ? "#1a5d4a"
                                        : "#e6f4ea",
                                      border: `1px solid ${theme.isDark ? "#4ade80" : "#137333"}`,
                                      borderRadius: 8,
                                      padding: "10px 18px",
                                      margin: "0 0 12px 0",
                                      color: theme.isDark
                                        ? "#4ade80"
                                        : "#137333",
                                      fontSize: 16,
                                      fontWeight: 500,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 6,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        marginBottom: 4,
                                      }}
                                    >
                                      {t("links")}
                                    </div>
                                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                                      {urls.map((url, i) => (
                                        <li
                                          key={url + i}
                                          style={{ marginBottom: 2 }}
                                        >
                                          <a
                                            href={
                                              url.startsWith("http")
                                                ? url
                                                : `https://${url}`
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              color: theme.primary,
                                              textDecoration: "underline",
                                              wordBreak: "break-all",
                                            }}
                                          >
                                            {url}
                                          </a>
                                        </li>
                                      ))}
                                    </ol>
                                  </div>
                                );
                              })()}
                              {/* Rating/Thank you UI */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  gap: 24,
                                  margin: "18px 0 4px 0",
                                }}
                              >
                                {showThankYou ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 12,
                                    }}
                                  >
                                    <div
                                      style={{
                                        color: "#19c37d",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {thankYouText}
                                    </div>
                                    <button
                                      style={{
                                        marginTop: 8,
                                        padding: "10px 24px",
                                        background: theme.primary,
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        fontSize: 16,
                                        cursor: "pointer",
                                        boxShadow: theme.isDark
                                          ? "0 2px 8px #0005"
                                          : "0 2px 8px #0002",
                                        transition: "background 0.2s",
                                      }}
                                      onClick={handleNewChatBtn}
                                    >
                                      {t("newChat") || "Новий чат"}
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      className="rate-btn"
                                      onClick={() => {
                                        const searchId =
                                          staticSearch && isStaticSearch
                                            ? staticSearch.id
                                            : message.search_id || message.id;
                                        handleRate("up", searchId);
                                        if (!(isStaticSearch && staticSearch)) {
                                          localStorage.setItem(
                                            `chat_rated_${searchId}`,
                                            "1",
                                          );
                                        }
                                      }}
                                      disabled={
                                        thisMsgRated ||
                                        (isStaticSearch && staticSearchRated)
                                      }
                                      aria-label={
                                        t("rateUp") || "Оцінити позитивно"
                                      }
                                    >
                                      👍
                                    </button>
                                    <button
                                      className="rate-btn"
                                      onClick={() => {
                                        const searchId =
                                          staticSearch && isStaticSearch
                                            ? staticSearch.id
                                            : message.search_id || message.id;
                                        handleRate("down", searchId);
                                        if (!(isStaticSearch && staticSearch)) {
                                          localStorage.setItem(
                                            `chat_rated_${searchId}`,
                                            "1",
                                          );
                                        }
                                      }}
                                      disabled={
                                        thisMsgRated ||
                                        (isStaticSearch && staticSearchRated)
                                      }
                                      aria-label={
                                        t("rateDown") || "Оцінити негативно"
                                      }
                                    >
                                      👎
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                      </div>
                    </div>
                  );
                })}
                {/* Archive notice below all messages if archived */}
                {isArchiveRoute && (
                  <div
                    style={{
                      textAlign: "center",
                      color: theme.textSecondary,
                      fontSize: 16,
                      marginTop: 32,
                    }}
                  >
                    {t("archivedChatNotice")}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area (hide if readOnly, archive route, or search answer is present) */}
          {!(
            readOnly ||
            isArchiveRoute ||
            displayChatWithSearch?.search?.answer
          ) && (
            <div style={inputAreaStyles}>
              <div style={inputContainerStyles}>
                <textarea
                  ref={inputRef}
                  value={inputText || ""}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={t("writeMessage")}
                  style={textareaStyles}
                  disabled={chatIsTyping || isInputDisabled}
                  rows={1}
                />
                <button
                  style={sendButtonStyles}
                  onClick={handleSend}
                  disabled={
                    !inputText?.trim() || chatIsTyping || isInputDisabled
                  }
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon-md"
                  >
                    <path d="M8.99992 16V6.41407L5.70696 9.70704C5.31643 10.0976 4.68342 10.0976 4.29289 9.70704C3.90237 9.31652 3.90237 8.6835 4.29289 8.29298L9.29289 3.29298L9.36907 3.22462C9.76184 2.90427 10.3408 2.92686 10.707 3.29298L15.707 8.29298L15.7753 8.36915C16.0957 8.76192 16.0731 9.34092 15.707 9.70704C15.3408 10.0732 14.7618 10.0958 14.3691 9.7754L14.2929 9.70704L10.9999 6.41407V16C10.9999 16.5523 10.5522 17 9.99992 17C9.44764 17 8.99992 16.5523 8.99992 16Z"></path>
                  </svg>
                </button>
              </div>
            </div>
          )}
          {/* Read-only notice for archive */}
          {isArchiveRoute && (
            <div
              style={{
                textAlign: "center",
                color: theme.textSecondary,
                fontSize: 14,
                margin: "16px 0",
              }}
            >
              {t("archiveReadOnlyNotice") ||
                "Цей чат архівований і доступний лише для перегляду."}
            </div>
          )}
        </div>
      </div>
      {/* Modal for medical disclaimer */}
      <DisclaimerModal
        open={showDisclaimer}
        onConfirm={handleDisclaimerConfirm}
        onCancel={handleDisclaimerCancel}
        text={t("medicalDisclaimer")}
      />
      {showWelcome && (
        <BasicModal
          type={"welcome"}
          open={showWelcome}
          title={t("welcomeTitle") || "Привіт!"}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              width="60"
              height="60"
              viewBox="0 0 100 100"
            >
              <path
                fill="#c7ede6"
                d="M87.2,56.7c1.1-2.2,1.8-4.6,1.8-7.2c0-6.6-4.2-12.3-10-14.5l0,0c0-11.6-9.4-21-21-21 c-9.8,0-18,6.7-20.3,15.8c-1.5-0.5-3-0.8-4.7-0.8c-7.7,0-14,5.8-14.9,13.3C12.9,43.4,9,48,9,53.5C9,59.9,14.1,65,20.5,65 c0.2,0,0.4,0,0.5,0c0,0.2,0,0.3,0,0.5C21,76.8,30.2,86,41.5,86c6.4,0,12.2-3,15.9-7.6c2.2,2.2,5.2,3.6,8.6,3.6 c4.7,0,8.7-2.7,10.7-6.5c1.1,0.3,2.2,0.5,3.3,0.5c6.1,0,11-4.9,11-11C91,61.7,89.5,58.7,87.2,56.7z"
              ></path>
              <path
                fill="#fdfcef"
                d="M79.9,60.5c0,0,3.6,0,6.1,0s4.5-2,4.5-4.5c0-2.3-1.8-4.2-4.1-4.5c0-0.2,0.1-0.4,0.1-0.5 c0-2.5-2-4.5-4.5-4.5c-1.4,0-2.7,0.7-3.5,1.7c-0.1-2.6-2.3-4.7-5-4.7c-2.8,0-5,2.2-5,5c0,0.4,0.1,0.9,0.2,1.3 C68,49,67.1,48.5,66,48.5c-1.8,0-3.2,1.3-3.5,3.1c-0.2,0-0.4-0.1-0.5-0.1c-2.5,0-4.5,2-4.5,4.5s2,4.5,4.5,4.5s9.5,0,9.5,0h5.4V61 h3V60.5z"
              ></path>
              <path
                fill="#472b29"
                d="M73.5,43c-3,0-5.5,2.5-5.5,5.5l0,0c-0.6-0.4-1.3-0.5-2-0.5c-1.8,0-3.4,1.3-3.9,3H62 c-2.8,0-5,2.2-5,5s2.2,5,5,5h14.9c0.3,0,0.5-0.2,0.5-0.5S77.2,60,76.9,60H62c-2.2,0-4-1.8-4-4s1.8-4,4-4c0.1,0,0.2,0,0.3,0h0.1 c0,0,0,0,0.1,0c0.2,0,0.5-0.2,0.5-0.4c0.2-1.5,1.5-2.6,3-2.6c0.9,0,1.7,0.4,2.3,1.1c0.1,0.1,0.2,0.2,0.4,0.2c0.1,0,0.2,0,0.2-0.1 c0.2-0.1,0.3-0.3,0.3-0.6c-0.1-0.4-0.2-0.8-0.2-1.2c0-2.5,2-4.5,4.5-4.5c2.4,0,4.3,1.9,4.5,4.3c0,0.2,0.2,0.4,0.3,0.5 c0.1,0,0.1,0,0.2,0c0.2,0,0.3-0.1,0.4-0.2c0.8-1,1.9-1.5,3.1-1.5c2.2,0,4,1.8,4,4c0,0.1,0,0.2,0,0.3v0.1c0,0.1,0,0.3,0.1,0.4 s0.2,0.2,0.3,0.2c2.1,0.2,3.6,1.9,3.6,4c0,2.2-1.8,4-4,4h-6.1c-0.3,0-0.5,0.2-0.5,0.5s0.2,0.5,0.5,0.5H86c2.8,0,5-2.2,5-5 c0-2.4-1.7-4.4-4-4.9V51c0-2.8-2.2-5-5-5c-1.2,0-2.3,0.4-3.2,1.2C78.2,44.8,76.1,43,73.5,43L73.5,43z"
              ></path>
              <path
                fill="#472b29"
                d="M72,50c-1.4,0-2.6,1-2.9,2.3c-0.4-0.2-0.8-0.3-1.2-0.3c-1.2,0-2.2,0.9-2.4,2.1 c-0.2,0-0.4-0.1-0.6-0.1c-1.5,0-2.8,1.2-2.9,2.7c0,0.1,0.1,0.3,0.2,0.3l0,0c0.1,0,0.2-0.1,0.2-0.2c0.1-1.3,1.2-2.3,2.4-2.3 c0.2,0,0.5,0,0.7,0.1c0,0,0,0,0.1,0s0.1,0,0.2-0.1c0.1,0,0.1-0.1,0.1-0.2c0-1,0.8-1.9,1.9-1.9c0.4,0,0.9,0.2,1.2,0.5 c0,0,0.1,0.1,0.2,0.1h0.1c0.1,0,0.1-0.1,0.2-0.2c0.1-1.3,1.2-2.3,2.5-2.3c0.2,0,0.4,0,0.7,0.1c0,0,0,0,0.1,0s0.2-0.1,0.2-0.2 c0-0.1,0-0.3-0.2-0.3C72.5,50,72.3,50,72,50L72,50z"
              ></path>
              <path
                fill="#472b29"
                d="M85.9,51.5c-1.3,0-2.5,0.9-2.9,2.2c0,0.1,0,0.3,0.2,0.3c0,0,0,0,0.1,0s0.2-0.1,0.2-0.2 c0.3-1.1,1.3-1.8,2.4-1.8c0.1,0,0.2,0,0.3,0l0,0c0.1,0,0.2-0.1,0.2-0.2c0-0.1-0.1-0.3-0.2-0.3C86.2,51.5,86,51.5,85.9,51.5 L85.9,51.5z"
              ></path>
              <path
                fill="#fff"
                d="M15.4,51H5.5C5.2,51,5,50.8,5,50.5S5.2,50,5.5,50h9.9c0.3,0,0.5,0.2,0.5,0.5S15.7,51,15.4,51z"
              ></path>
              <path
                fill="#fff"
                d="M18.5,51h-1c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h1c0.3,0,0.5,0.2,0.5,0.5S18.8,51,18.5,51z"
              ></path>
              <path
                fill="#fff"
                d="M23.5,53h-9c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h9c0.3,0,0.5,0.2,0.5,0.5S23.8,53,23.5,53z"
              ></path>
              <path
                fill="#fff"
                d="M12.5,53h-1c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h1c0.3,0,0.5,0.2,0.5,0.5S12.8,53,12.5,53z"
              ></path>
              <path
                fill="#fff"
                d="M9.5,53h-2C7.2,53,7,52.8,7,52.5S7.2,52,7.5,52h2c0.3,0,0.5,0.2,0.5,0.5S9.8,53,9.5,53z"
              ></path>
              <path
                fill="#fff"
                d="M15.5,55h-2c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h2c0.3,0,0.5,0.2,0.5,0.5S15.8,55,15.5,55z"
              ></path>
              <path
                fill="#fff"
                d="M18.5,46c-0.2,0-0.8,0-1,0c-0.3,0-0.5,0.2-0.5,0.5s0.2,0.5,0.5,0.5c0.2,0,0.8,0,1,0 c0.3,0,0.5-0.2,0.5-0.5S18.8,46,18.5,46z"
              ></path>
              <path
                fill="#fff"
                d="M18.5,48c-0.2,0-4.8,0-5,0c-0.3,0-0.5,0.2-0.5,0.5s0.2,0.5,0.5,0.5c0.2,0,4.8,0,5,0 c0.3,0,0.5-0.2,0.5-0.5S18.8,48,18.5,48z"
              ></path>
              <path
                fill="#fff"
                d="M23.5,50c-0.2,0-2.8,0-3,0c-0.3,0-0.5,0.2-0.5,0.5s0.2,0.5,0.5,0.5c0.2,0,2.8,0,3,0 c0.3,0,0.5-0.2,0.5-0.5S23.8,50,23.5,50z"
              ></path>
              <g>
                <path
                  fill="#fff"
                  d="M72.5,24h-10c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h10c0.3,0,0.5,0.2,0.5,0.5S72.8,24,72.5,24z"
                ></path>
                <path
                  fill="#fff"
                  d="M76.5,24h-2c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h2c0.3,0,0.5,0.2,0.5,0.5S76.8,24,76.5,24z"
                ></path>
                <path
                  fill="#fff"
                  d="M81.5,26h-10c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h10c0.3,0,0.5,0.2,0.5,0.5S81.8,26,81.5,26z"
                ></path>
                <path
                  fill="#fff"
                  d="M69.5,26h-1c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h1c0.3,0,0.5,0.2,0.5,0.5S69.8,26,69.5,26z"
                ></path>
                <path
                  fill="#fff"
                  d="M66.4,26h-1.9c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h1.9c0.3,0,0.5,0.2,0.5,0.5S66.7,26,66.4,26z"
                ></path>
                <path
                  fill="#fff"
                  d="M75.5,22h-5c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h5c0.3,0,0.5,0.2,0.5,0.5S75.8,22,75.5,22z"
                ></path>
                <path
                  fill="#fff"
                  d="M72.5,28h-2c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h2c0.3,0,0.5,0.2,0.5,0.5S72.8,28,72.5,28z"
                ></path>
              </g>
              <g>
                <circle cx="51" cy="50" r="23" fill="#78a2d2"></circle>
                <path
                  fill="#472b29"
                  d="M61.299,32.783c-0.043,0-0.087-0.011-0.127-0.035c-0.433-0.255-0.875-0.494-1.314-0.711 c-0.124-0.062-0.175-0.212-0.114-0.335c0.062-0.124,0.214-0.175,0.335-0.114c0.451,0.223,0.904,0.468,1.348,0.73 c0.119,0.069,0.158,0.223,0.088,0.342C61.468,32.739,61.384,32.783,61.299,32.783z"
                ></path>
                <path
                  fill="#472b29"
                  d="M63.295,34.129c-0.054,0-0.106-0.017-0.152-0.052c-0.215-0.165-0.435-0.325-0.656-0.481 c-0.113-0.079-0.141-0.235-0.061-0.348c0.08-0.113,0.237-0.141,0.348-0.061c0.228,0.16,0.453,0.324,0.674,0.493 c0.109,0.084,0.13,0.241,0.046,0.351C63.444,34.096,63.37,34.129,63.295,34.129z"
                ></path>
                <path
                  fill="#472b29"
                  d="M50.986,70.557c-11.327,0-20.543-9.216-20.543-20.543s9.216-20.543,20.543-20.543 c2.43,0,4.81,0.421,7.071,1.251c0.13,0.047,0.196,0.19,0.148,0.32c-0.047,0.13-0.19,0.198-0.32,0.148 c-2.207-0.81-4.528-1.22-6.899-1.22c-11.052,0-20.043,8.991-20.043,20.043s8.991,20.043,20.043,20.043s20.043-8.991,20.043-20.043 c0-5.639-2.396-11.047-6.571-14.84c-0.103-0.094-0.11-0.251-0.017-0.354c0.092-0.102,0.25-0.109,0.353-0.018 c4.28,3.888,6.735,9.432,6.735,15.211C71.529,61.341,62.313,70.557,50.986,70.557z"
                ></path>
                <path
                  fill="#472b29"
                  d="M51,73.987c-13.227,0-23.987-10.761-23.987-23.987S37.773,26.013,51,26.013S74.987,36.773,74.987,50 S64.227,73.987,51,73.987z M51,27.413c-12.454,0-22.587,10.133-22.587,22.587S38.546,72.587,51,72.587S73.587,62.454,73.587,50 S63.454,27.413,51,27.413z"
                ></path>
              </g>
              <g>
                <path
                  fill="#fdfcee"
                  d="M53.2,58.3c0,0.9,0.9,1.4,2.9,1.4v1.8H45.9v-1.8c1.9,0,2.8-0.5,2.8-1.4v-9.4c0-0.9-0.9-1.4-2.8-1.4 v-1.8h7.3C53.2,45.8,53.2,58.3,53.2,58.3z M53.6,40.6c0,0.7-0.3,1.3-0.8,1.9c-0.5,0.5-1.2,0.8-2,0.8c-0.4,0-0.8-0.1-1.1-0.2 c-0.3-0.1-0.6-0.3-0.9-0.6c-0.3-0.2-0.5-0.5-0.6-0.8C48,41.4,48,41,48,40.7c0-0.7,0.3-1.4,0.8-1.9c0.6-0.5,1.2-0.8,2-0.8 s1.4,0.3,2,0.8C53.4,39.2,53.6,39.8,53.6,40.6z"
                ></path>
                <path
                  fill="#472b29"
                  d="M56.6,62H45.4v-2.8h0.5c1.1,0,2.3-0.2,2.3-0.9v-9.4c0-0.6-0.8-0.9-2.3-0.9h-0.5v-2.8h8.3v13.1 c0,0.2,0,0.3,0.1,0.4c0.2,0.2,0.7,0.5,2.2,0.5h0.5V62H56.6z M46.4,61h9.2v-0.8c-1.2-0.1-2-0.3-2.5-0.8c-0.3-0.3-0.4-0.7-0.4-1.1 v-12h-6.3V47c1.9,0.1,2.8,0.7,2.8,1.9v9.4c0,1.2-1,1.8-2.8,1.9V61z M50.8,43.7c-0.5,0-0.9-0.1-1.3-0.3c-0.4-0.2-0.7-0.4-1-0.7 s-0.5-0.6-0.7-1s-0.3-0.8-0.3-1.3c0-0.9,0.3-1.6,1-2.2c0.7-0.6,1.4-0.9,2.3-0.9c0.9,0,1.7,0.3,2.3,0.9c0.7,0.6,1,1.4,1,2.2 c0,0.9-0.3,1.6-1,2.2C52.5,43.4,51.8,43.7,50.8,43.7z M50.8,38.4c-0.6,0-1.2,0.2-1.7,0.6c-0.5,0.4-0.7,0.9-0.7,1.5 c0,0.3,0.1,0.6,0.2,0.8c0.1,0.3,0.3,0.5,0.5,0.7s0.5,0.4,0.7,0.5c0.3,0.1,0.6,0.2,0.9,0.2c0.7,0,1.2-0.2,1.6-0.6 c0.4-0.4,0.7-0.9,0.7-1.5s-0.2-1.1-0.7-1.5C52,38.6,51.5,38.4,50.8,38.4z"
                ></path>
              </g>
              <g>
                <path
                  fill="#fdfcef"
                  d="M38.5,73.5c0,0,1.6,0,3.5,0s3.5-1.6,3.5-3.5c0-1.8-1.3-3.2-3.1-3.5c0-0.2,0.1-0.4,0.1-0.5 c0-1.9-1.6-3.5-3.5-3.5c-1,0-2,0.5-2.6,1.2c-0.4-1.8-2-3.2-3.9-3.2c-2.2,0-4,1.8-4,4c0,0.2,0,0.4,0.1,0.6c-0.4-0.3-1-0.6-1.6-0.6 c-1.2,0-2.2,0.9-2.5,2.1c-0.2,0-0.4-0.1-0.5-0.1c-1.9,0-3.5,1.6-3.5,3.5s1.6,3.5,3.5,3.5s7.5,0,7.5,0V74h7V73.5z"
                ></path>
                <path
                  fill="#472b29"
                  d="M40.3,69c-0.1,0-0.3-0.1-0.3-0.3c0-1.2,1-2.2,2.2-2.2c0,0,0.7,0,1.2,0.1c0.1,0,0.2,0.2,0.2,0.3 c0,0.1-0.2,0.2-0.3,0.2C42.9,67,42.2,67,42.2,67c-0.9,0-1.7,0.8-1.7,1.7C40.5,68.9,40.4,69,40.3,69z"
                ></path>
                <circle cx="33.5" cy="73.5" r=".5" fill="#472b29"></circle>
                <path
                  fill="#472b29"
                  d="M42,74h-3.5c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5H42c1.7,0,3-1.3,3-3c0-1.5-1.1-2.8-2.6-3 c-0.1,0-0.3-0.1-0.3-0.2S42,66.6,42,66.4c0-0.2,0-0.3,0-0.5c0-1.7-1.3-3-3-3c-0.8,0-1.6,0.4-2.2,1c-0.1,0.1-0.3,0.2-0.5,0.1 c-0.2,0-0.3-0.2-0.4-0.4c-0.3-1.6-1.8-2.8-3.4-2.8c-1.9,0-3.5,1.6-3.5,3.5c0,0.1,0,0.3,0,0.4c0,0.2-0.1,0.4-0.2,0.5 s-0.4,0.1-0.6,0c-0.3,0-0.8-0.2-1.2-0.2c-1,0-1.8,0.7-2,1.6c0,0.3-0.3,0.4-0.6,0.4c-0.2,0-0.3,0-0.5,0c-1.7,0-3,1.3-3,3s1.3,3,3,3 h7.5c0.3,0,0.5,0.2,0.5,0.5S31.8,74,31.5,74H24c-2.2,0-4-1.8-4-4s1.8-4,4-4c0.1,0,0.1,0,0.2,0c0.4-1.2,1.5-2,2.8-2 c0.3,0,0.7,0.1,1,0.2c0.2-2.3,2.1-4.2,4.5-4.2c1.8,0,3.5,1.1,4.2,2.8c0.7-0.5,1.5-0.8,2.3-0.8c2.2,0,4,1.8,4,4v0.1 c1.7,0.4,3,2,3,3.9C46,72.2,44.2,74,42,74z"
                ></path>
                <path
                  fill="#472b29"
                  d="M36.5,73c-0.2,0-0.8,0-1,0c-0.3,0-0.5,0.2-0.5,0.5s0.2,0.5,0.5,0.5c0.2,0,0.8,0,1,0 c0.3,0,0.5-0.2,0.5-0.5S36.8,73,36.5,73z"
                ></path>
              </g>
            </svg>
          }
          text=""
          subtitle={
            t("welcomeSubtitleText") ||
            "Мене звати АІ Начмед - я бот, який полегшить та спростить вашу роботу з відомою базою медичних даних PubMed."
          }
          paragraph={
            t("welcomeParagraphText") ||
            "Мої послуги є платними, але перші 3 питання ви можете задати безкоштовно!"
          }
          paymentParagraph={
            t("welcomePaymentParagraphText") ||
            "Після використання безкоштовних питань, щоб використовувати бота надалі, вам протрібно оформити місячну підписку на 10 питань - її вартість 10 $. Ви можете оплатити підписку за допомогою банківської картки."
          }
          onConfirm={() => setShowWelcome(false)}
          confirmText="OK"
        />
      )}
      {showPaywall && (
        <PaymentModal
          open={showPaywall}
          onClose={() => setShowPaywall(false)}
          paymentResponse={paymentResponse}
        />
      )}
      {subscriptionLimitModal && (
        <BasicModal
          type={"subscriptionLimit"}
          open={subscriptionLimitModal}
          title={t("needSubscription") || "Потрібна підписка"}
          icon={
            <svg
              height="30px"
              width="30px"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512.001 512.001"
              fill="#000000"
            >
              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></g>
              <g id="SVGRepo_iconCarrier">
                {" "}
                <polygon
                  style={{ fill: "#0bc9f9" }}
                  points="503.83,160.681 256,503.83 440.789,132.085 "
                ></polygon>{" "}
                <g>
                  {" "}
                  <polygon
                    style={{ fill: "#77d6ee" }}
                    points="465.702,160.681 256,503.83 305.926,128 "
                  ></polygon>{" "}
                  <polygon
                    style={{ fill: "#77d6ee" }}
                    points="206.075,128 256,503.83 8.171,160.681 "
                  ></polygon>{" "}
                </g>{" "}
                <polygon
                  style={{ fill: "#cae3f2" }}
                  points="338.607,160.681 256,503.83 173.394,160.681 256,106.213 "
                ></polygon>{" "}
                <polygon
                  style={{ fill: "#4193d2" }}
                  points="421.224,8.171 503.83,160.681 465.702,160.681 338.018,35.612 "
                ></polygon>{" "}
                <polygon
                  style={{ fill: "#77d6ee" }}
                  points="421.224,8.171 407.45,33.597 379.152,19.388 395.798,8.171 "
                ></polygon>{" "}
                <polygon
                  style={{ fill: "#0bc9f9" }}
                  points="407.45,33.597 465.702,160.681 8.171,160.681 90.777,8.171 173.394,84.774 256,8.171 338.607,84.774 "
                ></polygon>{" "}
                <g>
                  {" "}
                  <polygon
                    style={{ fill: "#cae3f2" }}
                    points="395.798,8.171 407.454,33.596 338.607,160.681 256,8.171 "
                  ></polygon>{" "}
                  <polygon
                    style={{ fill: "#cae3f2" }}
                    points="256,8.171 173.394,160.681 90.777,8.171 "
                  ></polygon>{" "}
                </g>{" "}
                <path d="M511.014,156.79L428.405,4.279c-0.02-0.036-0.045-0.068-0.065-0.102c-0.141-0.249-0.294-0.492-0.461-0.728 c-0.046-0.064-0.092-0.129-0.138-0.192c-0.196-0.26-0.403-0.512-0.631-0.749c-0.241-0.249-0.49-0.473-0.753-0.685 c-0.065-0.052-0.133-0.1-0.198-0.15c-0.195-0.148-0.399-0.289-0.61-0.42c-0.088-0.054-0.174-0.11-0.265-0.161 c-0.059-0.034-0.112-0.072-0.172-0.105c-0.182-0.099-0.368-0.182-0.553-0.265c-0.052-0.023-0.101-0.051-0.154-0.073 c-0.31-0.132-0.626-0.241-0.944-0.331c-0.059-0.016-0.119-0.029-0.179-0.045c-0.277-0.072-0.553-0.13-0.832-0.172 c-0.07-0.011-0.138-0.022-0.208-0.031c-0.357-0.046-0.715-0.072-1.071-0.07H90.817c-0.351-0.002-0.703,0.025-1.053,0.07 c-0.073,0.009-0.144,0.021-0.216,0.032c-0.277,0.042-0.553,0.099-0.828,0.171c-0.06,0.015-0.12,0.028-0.18,0.045 c-0.319,0.092-0.636,0.2-0.949,0.333c-0.044,0.019-0.085,0.042-0.129,0.061c-0.193,0.086-0.386,0.172-0.575,0.275 c-0.062,0.034-0.118,0.074-0.18,0.109c-0.083,0.048-0.163,0.098-0.244,0.148c-0.221,0.138-0.434,0.284-0.638,0.44 c-0.058,0.045-0.118,0.086-0.174,0.132c-0.532,0.43-0.999,0.919-1.399,1.453c-0.041,0.054-0.081,0.11-0.12,0.167 c-0.175,0.247-0.338,0.501-0.485,0.765c-0.015,0.028-0.036,0.052-0.051,0.081l-0.051,0.095C83.534,4.397,0.986,156.79,0.986,156.79 c-1.495,2.759-1.277,6.131,0.561,8.675l247.83,343.149c1.536,2.128,4,3.387,6.623,3.387c2.623,0,5.087-1.259,6.623-3.387 l247.83-343.149C512.291,162.921,512.509,159.549,511.014,156.79z M173.391,143.527L104.497,16.341h137.785L173.391,143.527z M256,25.325l68.892,127.186H187.108L256,25.325z M273.646,465.444l65.041-270.169c1.056-4.387-1.645-8.8-6.031-9.855 c-4.394-1.058-8.8,1.644-9.857,6.031L256,468.923L189.202,191.45c-1.058-4.387-5.463-7.087-9.857-6.031 c-4.386,1.056-7.086,5.469-6.031,9.855l65.041,270.169L17.799,160.059L90.78,25.326l68.893,127.185H46.687 c-4.513,0-8.17,3.658-8.17,8.17s3.657,8.17,8.17,8.17h418.629c4.513,0,8.17-3.658,8.17-8.17s-3.657-8.17-8.17-8.17H352.327 l26.989-49.825c2.149-3.967,0.674-8.926-3.292-11.076c-3.971-2.151-8.927-0.674-11.076,3.292l-26.339,48.625L269.718,16.341h137.785 l-26.988,49.825c-2.149,3.967-0.674,8.926,3.292,11.076c1.238,0.67,2.57,0.988,3.884,0.988c2.902,0,5.713-1.55,7.192-4.28 l26.339-48.625l72.981,134.734L273.646,465.444z"></path>{" "}
              </g>
            </svg>
          }
          text={t("subscriptionLimitText") || "Потрібна підписка!"}
          onConfirm={confirmPayment}
          onCancel={() => setSubscriptionLimitModal(false)}
          confirmText={t("buySubscription")}
          cancelText={t("cancel")}
        />
      )}
      {subscriptionExpiredModal && (
        <BasicModal
          type={"subscriptionExpired"}
          open={subscriptionExpiredModal}
          title={t("subscriptionExpiredTitle") || "Потрібна підписка"}
          icon={
            <svg
              height="30px"
              width="30px"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512.001 512.001"
              fill="#000000"
            >
              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></g>
              <g id="SVGRepo_iconCarrier">
                {" "}
                <polygon
                  style={{ fill: "#0bc9f9" }}
                  points="503.83,160.681 256,503.83 440.789,132.085 "
                ></polygon>{" "}
                <g>
                  {" "}
                  <polygon
                    style={{ fill: "#77d6ee" }}
                    points="465.702,160.681 256,503.83 305.926,128 "
                  ></polygon>{" "}
                  <polygon
                    style={{ fill: "#77d6ee" }}
                    points="206.075,128 256,503.83 8.171,160.681 "
                  ></polygon>{" "}
                </g>{" "}
                <polygon
                  style={{ fill: "#cae3f2" }}
                  points="338.607,160.681 256,503.83 173.394,160.681 256,106.213 "
                ></polygon>{" "}
                <polygon
                  style={{ fill: "#4193d2" }}
                  points="421.224,8.171 503.83,160.681 465.702,160.681 338.018,35.612 "
                ></polygon>{" "}
                <polygon
                  style={{ fill: "#77d6ee" }}
                  points="421.224,8.171 407.45,33.597 379.152,19.388 395.798,8.171 "
                ></polygon>{" "}
                <polygon
                  style={{ fill: "#0bc9f9" }}
                  points="407.45,33.597 465.702,160.681 8.171,160.681 90.777,8.171 173.394,84.774 256,8.171 338.607,84.774 "
                ></polygon>{" "}
                <g>
                  {" "}
                  <polygon
                    style={{ fill: "#cae3f2" }}
                    points="395.798,8.171 407.454,33.596 338.607,160.681 256,8.171 "
                  ></polygon>{" "}
                  <polygon
                    style={{ fill: "#cae3f2" }}
                    points="256,8.171 173.394,160.681 90.777,8.171 "
                  ></polygon>{" "}
                </g>{" "}
                <path d="M511.014,156.79L428.405,4.279c-0.02-0.036-0.045-0.068-0.065-0.102c-0.141-0.249-0.294-0.492-0.461-0.728 c-0.046-0.064-0.092-0.129-0.138-0.192c-0.196-0.26-0.403-0.512-0.631-0.749c-0.241-0.249-0.49-0.473-0.753-0.685 c-0.065-0.052-0.133-0.1-0.198-0.15c-0.195-0.148-0.399-0.289-0.61-0.42c-0.088-0.054-0.174-0.11-0.265-0.161 c-0.059-0.034-0.112-0.072-0.172-0.105c-0.182-0.099-0.368-0.182-0.553-0.265c-0.052-0.023-0.101-0.051-0.154-0.073 c-0.31-0.132-0.626-0.241-0.944-0.331c-0.059-0.016-0.119-0.029-0.179-0.045c-0.277-0.072-0.553-0.13-0.832-0.172 c-0.07-0.011-0.138-0.022-0.208-0.031c-0.357-0.046-0.715-0.072-1.071-0.07H90.817c-0.351-0.002-0.703,0.025-1.053,0.07 c-0.073,0.009-0.144,0.021-0.216,0.032c-0.277,0.042-0.553,0.099-0.828,0.171c-0.06,0.015-0.12,0.028-0.18,0.045 c-0.319,0.092-0.636,0.2-0.949,0.333c-0.044,0.019-0.085,0.042-0.129,0.061c-0.193,0.086-0.386,0.172-0.575,0.275 c-0.062,0.034-0.118,0.074-0.18,0.109c-0.083,0.048-0.163,0.098-0.244,0.148c-0.221,0.138-0.434,0.284-0.638,0.44 c-0.058,0.045-0.118,0.086-0.174,0.132c-0.532,0.43-0.999,0.919-1.399,1.453c-0.041,0.054-0.081,0.11-0.12,0.167 c-0.175,0.247-0.338,0.501-0.485,0.765c-0.015,0.028-0.036,0.052-0.051,0.081l-0.051,0.095C83.534,4.397,0.986,156.79,0.986,156.79 c-1.495,2.759-1.277,6.131,0.561,8.675l247.83,343.149c1.536,2.128,4,3.387,6.623,3.387c2.623,0,5.087-1.259,6.623-3.387 l247.83-343.149C512.291,162.921,512.509,159.549,511.014,156.79z M173.391,143.527L104.497,16.341h137.785L173.391,143.527z M256,25.325l68.892,127.186H187.108L256,25.325z M273.646,465.444l65.041-270.169c1.056-4.387-1.645-8.8-6.031-9.855 c-4.394-1.058-8.8,1.644-9.857,6.031L256,468.923L189.202,191.45c-1.058-4.387-5.463-7.087-9.857-6.031 c-4.386,1.056-7.086,5.469-6.031,9.855l65.041,270.169L17.799,160.059L90.78,25.326l68.893,127.185H46.687 c-4.513,0-8.17,3.658-8.17,8.17s3.657,8.17,8.17,8.17h418.629c4.513,0,8.17-3.658,8.17-8.17s-3.657-8.17-8.17-8.17H352.327 l26.989-49.825c2.149-3.967,0.674-8.926-3.292-11.076c-3.971-2.151-8.927-0.674-11.076,3.292l-26.339,48.625L269.718,16.341h137.785 l-26.988,49.825c-2.149,3.967-0.674,8.926,3.292,11.076c1.238,0.67,2.57,0.988,3.884,0.988c2.902,0,5.713-1.55,7.192-4.28 l26.339-48.625l72.981,134.734L273.646,465.444z"></path>{" "}
              </g>
            </svg>
          }
          text={t("subscriptionExpiredText") || "Потрібна підписка!"}
          onConfirm={confirmPayment}
          onCancel={() => setSubscriptionExpiredModal(false)}
          confirmText={t("buySubscription")}
          cancelText={t("cancel")}
        />
      )}
      {paymentRequiredModal && (
        <BasicModal
          type={"paymentRequired"}
          open={paymentRequiredModal}
          title={t("paymentRequired") || "Потрібна оплата"}
          icon={
            <svg
              height="30px"
              width="30px"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512.001 512.001"
              fill="#000000"
            >
              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></g>
              <g id="SVGRepo_iconCarrier">
                {" "}
                <polygon
                  style={{ fill: "#0bc9f9" }}
                  points="503.83,160.681 256,503.83 440.789,132.085 "
                ></polygon>{" "}
                <g>
                  {" "}
                  <polygon
                    style={{ fill: "#77d6ee" }}
                    points="465.702,160.681 256,503.83 305.926,128 "
                  ></polygon>{" "}
                  <polygon
                    style={{ fill: "#77d6ee" }}
                    points="206.075,128 256,503.83 8.171,160.681 "
                  ></polygon>{" "}
                </g>{" "}
                <polygon
                  style={{ fill: "#cae3f2" }}
                  points="338.607,160.681 256,503.83 173.394,160.681 256,106.213 "
                ></polygon>{" "}
                <polygon
                  style={{ fill: "#4193d2" }}
                  points="421.224,8.171 503.83,160.681 465.702,160.681 338.018,35.612 "
                ></polygon>{" "}
                <polygon
                  style={{ fill: "#77d6ee" }}
                  points="421.224,8.171 407.45,33.597 379.152,19.388 395.798,8.171 "
                ></polygon>{" "}
                <polygon
                  style={{ fill: "#0bc9f9" }}
                  points="407.45,33.597 465.702,160.681 8.171,160.681 90.777,8.171 173.394,84.774 256,8.171 338.607,84.774 "
                ></polygon>{" "}
                <g>
                  {" "}
                  <polygon
                    style={{ fill: "#cae3f2" }}
                    points="395.798,8.171 407.454,33.596 338.607,160.681 256,8.171 "
                  ></polygon>{" "}
                  <polygon
                    style={{ fill: "#cae3f2" }}
                    points="256,8.171 173.394,160.681 90.777,8.171 "
                  ></polygon>{" "}
                </g>{" "}
                <path d="M511.014,156.79L428.405,4.279c-0.02-0.036-0.045-0.068-0.065-0.102c-0.141-0.249-0.294-0.492-0.461-0.728 c-0.046-0.064-0.092-0.129-0.138-0.192c-0.196-0.26-0.403-0.512-0.631-0.749c-0.241-0.249-0.49-0.473-0.753-0.685 c-0.065-0.052-0.133-0.1-0.198-0.15c-0.195-0.148-0.399-0.289-0.61-0.42c-0.088-0.054-0.174-0.11-0.265-0.161 c-0.059-0.034-0.112-0.072-0.172-0.105c-0.182-0.099-0.368-0.182-0.553-0.265c-0.052-0.023-0.101-0.051-0.154-0.073 c-0.31-0.132-0.626-0.241-0.944-0.331c-0.059-0.016-0.119-0.029-0.179-0.045c-0.277-0.072-0.553-0.13-0.832-0.172 c-0.07-0.011-0.138-0.022-0.208-0.031c-0.357-0.046-0.715-0.072-1.071-0.07H90.817c-0.351-0.002-0.703,0.025-1.053,0.07 c-0.073,0.009-0.144,0.021-0.216,0.032c-0.277,0.042-0.553,0.099-0.828,0.171c-0.06,0.015-0.12,0.028-0.18,0.045 c-0.319,0.092-0.636,0.2-0.949,0.333c-0.044,0.019-0.085,0.042-0.129,0.061c-0.193,0.086-0.386,0.172-0.575,0.275 c-0.062,0.034-0.118,0.074-0.18,0.109c-0.083,0.048-0.163,0.098-0.244,0.148c-0.221,0.138-0.434,0.284-0.638,0.44 c-0.058,0.045-0.118,0.086-0.174,0.132c-0.532,0.43-0.999,0.919-1.399,1.453c-0.041,0.054-0.081,0.11-0.12,0.167 c-0.175,0.247-0.338,0.501-0.485,0.765c-0.015,0.028-0.036,0.052-0.051,0.081l-0.051,0.095C83.534,4.397,0.986,156.79,0.986,156.79 c-1.495,2.759-1.277,6.131,0.561,8.675l247.83,343.149c1.536,2.128,4,3.387,6.623,3.387c2.623,0,5.087-1.259,6.623-3.387 l247.83-343.149C512.291,162.921,512.509,159.549,511.014,156.79z M173.391,143.527L104.497,16.341h137.785L173.391,143.527z M256,25.325l68.892,127.186H187.108L256,25.325z M273.646,465.444l65.041-270.169c1.056-4.387-1.645-8.8-6.031-9.855 c-4.394-1.058-8.8,1.644-9.857,6.031L256,468.923L189.202,191.45c-1.058-4.387-5.463-7.087-9.857-6.031 c-4.386,1.056-7.086,5.469-6.031,9.855l65.041,270.169L17.799,160.059L90.78,25.326l68.893,127.185H46.687 c-4.513,0-8.17,3.658-8.17,8.17s3.657,8.17,8.17,8.17h418.629c4.513,0,8.17-3.658,8.17-8.17s-3.657-8.17-8.17-8.17H352.327 l26.989-49.825c2.149-3.967,0.674-8.926-3.292-11.076c-3.971-2.151-8.927-0.674-11.076,3.292l-26.339,48.625L269.718,16.341h137.785 l-26.988,49.825c-2.149,3.967-0.674,8.926,3.292,11.076c1.238,0.67,2.57,0.988,3.884,0.988c2.902,0,5.713-1.55,7.192-4.28 l26.339-48.625l72.981,134.734L273.646,465.444z"></path>{" "}
              </g>
            </svg>
          }
          text={t("paymentRequiredText") || "Для створення нового чату потрібна підписка. Будь ласка, придбайте підписку для продовження."}
          onConfirm={confirmPayment}
          onCancel={() => setPaymentRequiredModal(false)}
          confirmText={t("buySubscription")}
          cancelText={t("cancel")}
        />
      )}
    </>
  );
};

export default ChatScreen;
